import React, { useEffect, useMemo, useRef, useState } from "react";
import ScrollToTop from "../ScrollToTop";
import AdminSidebar from "../../Component/AdminSidebar";
import {
  deleteCategory,
  deleteProductVariant,
  deliveryRules,
  formatAdminPrice,
  generateInternalBarcode,
  importPrestashopProductCsv,
  loadCatalog,
  loadCategories,
  loadVariantsForProduct,
  LOW_STOCK_THRESHOLD,
  saveCategory,
  saveCatalog,
  saveProductVariant,
  getProductBusinessUnit,
} from "../../data/catalog";
import { categoryTree, traiteurCategoryTree } from "../../data/categoryTree";
import { loadMenus, saveMenu, deleteMenu } from "../../data/menus";
import { loadDeliveryZones } from "../../data/delivery";
import {
  couponTypeOptions,
  deleteCoupon,
  loadCoupons,
  saveCoupon,
} from "../../data/coupons";
import { loadGiftCards, saveGiftCard, deleteGiftCard, createGiftCardBatch, giftCardStatusOptions, giftCardStatusBadgeClass, giftCardStatusBadgeLabel, getGiftCardTransactions } from "../../data/giftCards";
import { loadLoyaltyPoints, getLoyaltyTransactions, adjustPoints } from "../../data/loyaltyPoints";
import { printProductLabel } from "../../data/labels";
import { loadTaxRates, computeTTC } from "../../data/taxRates";
import { loadSuppliers } from "../../data/suppliers";
import { isSupabaseConfigured, supabase } from "../../lib/supabase";
import JsBarcode from "jsbarcode";
import { barcodeFormatFor } from "../../data/labels";

import { getCustomerGroups } from "../../data/customerGroups";

const resizeImage = (file, maxWidth = 800) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ratio = Math.min(maxWidth / img.width, 1);
        canvas.width = img.width * ratio;
        canvas.height = img.height * ratio;
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", 0.8));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });

const uploadProductImage = async (file) => {
  const dataUrl = await resizeImage(file);
  if (isSupabaseConfigured && supabase) {
    const fileName = `product-${Date.now()}-${file.name.replace(/[^a-z0-9.]/gi, "_")}`;
    const base64 = dataUrl.split(",")[1];
    const { error } = await supabase.storage
      .from("product-images")
      .upload(fileName, Uint8Array.from(atob(base64), (c) => c.charCodeAt(0)), {
        contentType: "image/jpeg",
        upsert: true,
      });
    if (!error) {
      const { data: urlData } = supabase.storage.from("product-images").getPublicUrl(fileName);
      return urlData?.publicUrl || dataUrl;
    }
  }
  return dataUrl;
};

const emptyForm = {
  purchasePriceHT: "",
  priceHT: "",
  priceTTC: "",
  unitPriceHT: "",
  unitPriceUnit: "",
  onSale: false,
  name: "",
  businessUnit: "alimentation",
  category: [],
  price: "",
  purchasePrice: "",
  priceOverrides: {},
  specificPrices: [],
  stock: "",
  sku: "",
  barcode: "",
  type: "regular",
  featured: false,
  saleType: "unit",
  antiGaspiDiscount: "",
  antiGaspiReason: "",
  antiGaspiMaxQty: "",
  antiGaspiStartsAt: "",
  antiGaspiExpiresAt: "",
  unitType: "piece",
  imageUrl: "",
  description: "",
  sortOrder: "",
  variantLabel: "",
  variantWeightValue: "",
  variantWeightUnit: "g",
  minWeightKg: "",
  stepWeightKg: "",
  approximateWeightKg: "",
  maxPerOrder: "",
  allowedZoneIds: [],
  tax_rate_id: "",
  supplierId: "",
};

const emptyCategoryForm = {
  id: "",
  name: "",
  description: "",
  sort_order: 0,
};

const emptyVariantForm = {
  id: "",
  product_id: "",
  sku: "",
  name: "",
  size_label: "",
  weight_value: "",
  weight_unit: "g",
  price_override: "",
  is_default: false,
};

const emptyMenuForm = {
  id: "",
  name: "",
  price: "",
  businessUnit: "traiteur",
  componentsText: "",
  sortOrder: 0,
};

const MyAccountAddress = () => {
  const [loaderStatus, setLoaderStatus] = useState(true);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [deliveryZones, setDeliveryZones] = useState([]);
  const [taxRates, setTaxRates] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [customerGroups, setCustomerGroups] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [importMessage, setImportMessage] = useState("");
  const [categoryForm, setCategoryForm] = useState(emptyCategoryForm);
  const [variantForm, setVariantForm] = useState(emptyVariantForm);
  const [variants, setVariants] = useState([]);
  const [selectedVariantProductId, setSelectedVariantProductId] = useState("");
  const [categoryMessage, setCategoryMessage] = useState("");
  const [categoryListUnit, setCategoryListUnit] = useState("alimentation");
  const [variantMessage, setVariantMessage] = useState("");
  const [productSearch, setProductSearch] = useState("");
  const [unitFilter, setUnitFilter] = useState("all");
  const [productPage, setProductPage] = useState(1);
  const [productMessage, setProductMessage] = useState("");
  const [productError, setProductError] = useState("");
  const [activeTab, setActiveTab] = useState("produits");
  const [productFormTab, setProductFormTab] = useState("informations");
  const [coupons, setCoupons] = useState([]);
  const [couponForm, setCouponForm] = useState({ code: "", type: "percent", value: "", minOrder: "", maxUses: "", description: "", active: true });
  const [editingCouponId, setEditingCouponId] = useState(null);
  const [couponMessage, setCouponMessage] = useState("");
  const [giftCards, setGiftCards] = useState([]);
  const [giftCardForm, setGiftCardForm] = useState({ balance: "", expiryDate: "", minOrder: "", count: 1, note: "", recipientName: "" });
  const [editingGiftCardId, setEditingGiftCardId] = useState(null);
  const [giftCardEditForm, setGiftCardEditForm] = useState({});
  const [giftCardMessage, setGiftCardMessage] = useState("");
  const [giftCardSearch, setGiftCardSearch] = useState("");
  const [giftCardStatusFilter, setGiftCardStatusFilter] = useState("all");
  const [expandedGiftCardId, setExpandedGiftCardId] = useState(null);
  const [giftCardTransactions, setGiftCardTransactions] = useState([]);
  const [createdGiftCardCodes, setCreatedGiftCardCodes] = useState([]);
  const [loyaltyPointsList, setLoyaltyPointsList] = useState([]);
  const [menus, setMenus] = useState([]);
  const [menuForm, setMenuForm] = useState(emptyMenuForm);
  const [editingMenuId, setEditingMenuId] = useState(null);
  const [menuMessage, setMenuMessage] = useState("");
  const [loyaltySearch, setLoyaltySearch] = useState("");
  const [selectedLoyaltyCustomer, setSelectedLoyaltyCustomer] = useState(null);
  const [loyaltyTransactions, setLoyaltyTransactions] = useState([]);
  const [loyaltyAdjustAmount, setLoyaltyAdjustAmount] = useState("");
  const [loyaltyAdjustNote, setLoyaltyAdjustNote] = useState("");
  const [loyaltyMessage, setLoyaltyMessage] = useState("");
  const [earnedThisMonth, setEarnedThisMonth] = useState(0);
  const [redeemedThisMonth, setRedeemedThisMonth] = useState(0);
  const productFormCardRef = useRef(null);
  const productNameInputRef = useRef(null);
  const barcodePreviewRef = useRef(null);
  const recentlyCreatedPreviewRef = useRef(null);
  const [recentlyCreated, setRecentlyCreated] = useState(null);

  const PRODUCTS_PER_PAGE = 10;

  useEffect(() => {
    let isMounted = true;

    const fetchProducts = async () => {
      setCustomerGroups(getCustomerGroups());
      const [loadedProducts, loadedCategories, loadedZones, loadedCoupons, loadedGiftCards, loadedLoyaltyPoints, loadedTaxRates, loadedSuppliers, loadedMenus] = await Promise.all([
        loadCatalog(),
        loadCategories(),
        loadDeliveryZones(),
        loadCoupons(),
        loadGiftCards(),
        loadLoyaltyPoints(),
        loadTaxRates(),
        loadSuppliers(),
        loadMenus(),
      ]);
      if (!isMounted) {
        return;
      }
      setProducts(loadedProducts);
      setCategories(loadedCategories);
      setDeliveryZones(loadedZones);
      setCoupons(loadedCoupons);
      setGiftCards(loadedGiftCards);
      setLoyaltyPointsList(loadedLoyaltyPoints);
      setTaxRates(loadedTaxRates);
      setSuppliers(loadedSuppliers);
      setMenus(loadedMenus);
      setSelectedVariantProductId((current) => current || loadedProducts[0]?.id || "");
      setLoaderStatus(false);
    };

    fetchProducts();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchVariants = async () => {
      const loadedVariants = await loadVariantsForProduct(selectedVariantProductId);
      if (!isMounted) {
        return;
      }
      setVariants(loadedVariants);
      setVariantForm((current) => ({
        ...emptyVariantForm,
        product_id: selectedVariantProductId,
      }));
    };

    fetchVariants();

    return () => {
      isMounted = false;
    };
  }, [selectedVariantProductId]);

  useEffect(() => {
    const now = new Date();
    const thisMonthStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    getLoyaltyTransactions("").then((all) => {
      const monthTxns = all.filter((t) => t.createdAt && t.createdAt.startsWith(thisMonthStr));
      setEarnedThisMonth(monthTxns.filter((t) => t.type === "earn").reduce((s, t) => s + t.points, 0));
      setRedeemedThisMonth(monthTxns.filter((t) => t.type === "redeem").reduce((s, t) => s + t.points, 0));
    });
  }, [loyaltyPointsList]);

  const categorySummary = useMemo(() => {
    const groups = products.reduce((acc, product) => {
      acc[product.category] = (acc[product.category] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(groups).map(([name, count]) => ({
      name,
      count,
      status: "Actif",
    }));
  }, [products]);

  const lowStockCount = useMemo(
    () => products.filter((product) => product.stock <= LOW_STOCK_THRESHOLD).length,
    [products]
  );

  const featuredCount = useMemo(
    () => products.filter((product) => product.featured).length,
    [products]
  );

  const filteredProducts = useMemo(() => {
    const term = productSearch.trim().toLowerCase();

    let list = !term
      ? products
      : products.filter((product) => {
          const terms = term.split(/\s+/);
          const source = `${product.name} ${product.category} ${product.sku} ${product.barcode || ""} ${product.type}`.toLowerCase();
          return terms.every(t => source.includes(t));
        });

    if (unitFilter !== "all") {
      list = list.filter((product) => getProductBusinessUnit(product) === unitFilter);
    }

    return [...list].sort((a, b) => {
      const orderA = a.sortOrder === "" || a.sortOrder === undefined || a.sortOrder === null ? Infinity : Number(a.sortOrder);
      const orderB = b.sortOrder === "" || b.sortOrder === undefined || b.sortOrder === null ? Infinity : Number(b.sortOrder);
      if (orderA !== orderB) return orderA - orderB;
      return (a.name || "").localeCompare(b.name || "");
    });
  }, [productSearch, products, unitFilter]);

  const totalProductPages = Math.max(
    1,
    Math.ceil(filteredProducts.length / PRODUCTS_PER_PAGE)
  );

  const paginatedProducts = useMemo(() => {
    const startIndex = (productPage - 1) * PRODUCTS_PER_PAGE;
    return filteredProducts.slice(startIndex, startIndex + PRODUCTS_PER_PAGE);
  }, [filteredProducts, productPage]);

  const handleChange = (event) => {
    const { name, value, type, checked, options } = event.target;
    if (type === "select-multiple") {
      const values = Array.from(options).filter(opt => opt.selected).map(opt => opt.value);
      setForm((current) => ({
        ...current,
        [name]: values,
      }));
    } else {
      setForm((current) => ({
        ...current,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
    setProductMessage("");
    setProductError("");
  };

  const handleAllowedZoneToggle = (zoneId) => {
    setForm((current) => ({
      ...current,
      allowedZoneIds: current.allowedZoneIds.includes(zoneId)
        ? current.allowedZoneIds.filter((id) => id !== zoneId)
        : [...current.allowedZoneIds, zoneId],
    }));
  };

  const handleCategoryChange = (event) => {
    const { name, value } = event.target;
    setCategoryForm((current) => ({
      ...current,
      [name]: name === "sort_order" ? value : value,
    }));
    setCategoryMessage("");
  };

  const handleProductCategoryToggle = (categoryName) => {
    setForm((current) => {
      const selected = Array.isArray(current.category) ? current.category : [];
      return {
        ...current,
        category: selected.includes(categoryName)
          ? selected.filter((c) => c !== categoryName)
          : [...selected, categoryName],
      };
    });
    setProductMessage("");
    setProductError("");
  };

  const handleVariantChange = (event) => {
    const { name, value, type, checked } = event.target;
    setVariantForm((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
    setVariantMessage("");
  };

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
    setProductMessage("");
    setProductError("");
  };

  const resetCategoryForm = () => {
    setCategoryForm(emptyCategoryForm);
  };

  const resetVariantForm = () => {
    setVariantForm({
      ...emptyVariantForm,
      product_id: selectedVariantProductId,
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const nextProduct = {
      id: editingId || undefined,
      name: form.name.trim(),
      businessUnit: form.businessUnit || "alimentation",
      category: Array.isArray(form.category)
        ? form.category.map((c) => String(c).trim()).filter(Boolean).join(", ")
        : String(form.category || "").trim(),
      price: Number(form.price),
      purchasePrice: form.purchasePrice ? Number(form.purchasePrice) : 0,
      priceOverrides: form.priceOverrides || {},
      specificPrices: form.specificPrices || [],
      stock: Number(form.stock),
      sku: form.sku.trim(),
      barcode: form.barcode.trim(),
      type: form.type,
      featured: form.featured,
      saleType: form.saleType,
      antiGaspiDiscount:
        form.antiGaspiDiscount === "" ? undefined : Number(form.antiGaspiDiscount),
      antiGaspiReason: form.antiGaspiReason ? form.antiGaspiReason.trim() : "",
      antiGaspiMaxQty:
        form.antiGaspiMaxQty === "" ? undefined : Math.max(1, Math.round(Number(form.antiGaspiMaxQty))),
      antiGaspiStartsAt: form.antiGaspiStartsAt || "",
      antiGaspiExpiresAt: form.antiGaspiExpiresAt || "",
      unitType: form.unitType,
      imageUrl: form.imageUrl.trim(),
      description: form.description.trim(),
      sortOrder: form.sortOrder,
      minWeightKg: form.minWeightKg,
      stepWeightKg: form.stepWeightKg,
      approximateWeightKg: form.approximateWeightKg,
      maxPerOrder: form.maxPerOrder,
      allowedZoneIds: form.allowedZoneIds,
      variantLabel: form.variantLabel.trim(),
      variantWeightValue: form.variantWeightValue,
      variantWeightUnit: form.variantWeightUnit,
      tax_rate_id: form.tax_rate_id || "",
      purchasePriceHT: form.purchasePriceHT ? Number(form.purchasePriceHT) : (form.purchasePrice ? Number(form.purchasePrice) : 0),
      priceHT: form.priceHT ? Number(form.priceHT) : (form.price ? Number(form.price) : 0),
      priceTTC: form.priceTTC ? Number(form.priceTTC) : undefined,
      unitPriceHT: form.unitPriceHT ? Number(form.unitPriceHT) : undefined,
      unitPriceUnit: form.unitPriceUnit || "",
      onSale: Boolean(form.onSale),
      supplierId: form.supplierId || "",
    };

    if (!nextProduct.category) {
      setProductError("Veuillez selectionner au moins une categorie.");
      return;
    }

    const normalizedBarcode = nextProduct.barcode.trim();
    if (normalizedBarcode) {
      const duplicateBarcode = products.find(
        (product) =>
          product.id !== editingId &&
          String(product.barcode || "").trim() === normalizedBarcode
      );

      if (duplicateBarcode) {
        setProductError(`Ce code-barres est deja utilise par ${duplicateBarcode.name}.`);
        return;
      }
    }

    const nextProducts = editingId
      ? products.map((product) => (product.id === editingId ? { ...product, ...nextProduct } : product))
      : [...products, nextProduct];

    const saved = await saveCatalog(nextProducts);
    setProducts(saved);
    if (!selectedVariantProductId) {
      setSelectedVariantProductId(saved[0]?.id || "");
    }
    resetForm();
    setProductMessage(editingId ? "Produit mis a jour." : "Produit ajoute.");
    if (!editingId) {
      setRecentlyCreated({ ...nextProduct, id: nextProduct.id ? nextProduct.id : `custom-${Date.now()}` });
    } else {
      setRecentlyCreated(null);
    }
  };

  const handleEdit = (product) => {
    setActiveTab("ajouter");
    setProductFormTab("informations");
    setEditingId(product.id);
    setProductError("");
    setProductMessage(`Modification de "${product.name}"`);
    setForm({
      name: product.name,
      businessUnit: product.businessUnit || "alimentation",
      category: Array.isArray(product.category)
        ? product.category.map((c) => String(c).trim()).filter(Boolean)
        : String(product.category || "").split(",").map((c) => c.trim()).filter(Boolean),
      price: product.price,
      purchasePrice: product.purchasePrice || "",
      priceOverrides: product.priceOverrides || {},
      specificPrices: product.specificPrices || [],
      stock: product.stock,
      sku: product.sku,
      barcode: product.barcode || "",
      type: product.type,
      featured: product.featured,
      saleType: product.saleType || "unit",
      antiGaspiDiscount: product.antiGaspiDiscount ?? "",
      antiGaspiReason: product.antiGaspiReason || "",
      antiGaspiMaxQty: product.antiGaspiMaxQty ?? "",
      antiGaspiStartsAt: product.antiGaspiStartsAt || "",
      antiGaspiExpiresAt: product.antiGaspiExpiresAt || "",
      unitType: product.unitType || "piece",
      imageUrl: product.imageUrl || "",
      description: product.description || "",
      sortOrder: product.sortOrder ?? "",
      minWeightKg: product.minWeightKg ?? "",
      stepWeightKg: product.stepWeightKg ?? "",
      approximateWeightKg: product.approximateWeightKg ?? "",
      maxPerOrder: product.maxPerOrder ?? "",
      allowedZoneIds: product.allowedZoneIds || [],
      variantLabel: product.variantLabel || "",
      variantWeightValue: product.variantWeightValue ?? "",
      variantWeightUnit: product.variantWeightUnit || "g",
      tax_rate_id: product.tax_rate_id || "",
      purchasePriceHT: product.purchasePriceHT || product.purchasePrice || "",
      priceHT: product.priceHT || product.price || "",
      priceTTC: product.priceTTC || "",
      unitPriceHT: product.unitPriceHT || "",
      unitPriceUnit: product.unitPriceUnit || "",
      onSale: Boolean(product.onSale || false),
      supplierId: product.supplierId || "",
    });

    if (typeof window !== "undefined") {
      window.requestAnimationFrame(() => {
        productFormCardRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });

        window.requestAnimationFrame(() => {
          productNameInputRef.current?.focus();
          productNameInputRef.current?.select?.();
        });
      });
    }
  };

  const handleCategorySubmit = async (event) => {
    event.preventDefault();

    const savedCategory = await saveCategory(categoryForm);
    const refreshedCategories = await loadCategories();
    setCategories(refreshedCategories);
    setCategoryForm({
      id: savedCategory.id,
      name: savedCategory.name,
      description: savedCategory.description || "",
      sort_order: savedCategory.sort_order || 0,
    });
    setCategoryMessage("Categorie enregistree.");
  };

  const handleCategoryEdit = (category) => {
    setCategoryForm({
      id: category.id,
      name: category.name,
      description: category.description || "",
      sort_order: category.sort_order || 0,
    });
    setCategoryMessage("");
  };

  const handleCategoryDelete = async (id) => {
    await deleteCategory(id);
    const refreshedCategories = await loadCategories();
    setCategories(refreshedCategories);
    if (categoryForm.id === id) {
      resetCategoryForm();
    }
    setCategoryMessage("Categorie supprimee.");
  };

  const handleVariantSubmit = async (event) => {
    event.preventDefault();
    if (!selectedVariantProductId) {
      return;
    }

    const savedVariant = await saveProductVariant({
      ...variantForm,
      product_id: selectedVariantProductId,
    });

    const refreshedVariants = await loadVariantsForProduct(selectedVariantProductId);
    const refreshedProducts = await loadCatalog();
    setVariants(refreshedVariants);
    setProducts(refreshedProducts);
    setVariantForm({
      id: savedVariant.id,
      product_id: savedVariant.product_id,
      sku: savedVariant.sku,
      name: savedVariant.name,
      size_label: savedVariant.size_label || "",
      weight_value: savedVariant.weight_value ?? "",
      weight_unit: savedVariant.weight_unit || "g",
      price_override: savedVariant.price_override ?? "",
      is_default: savedVariant.is_default,
    });
    setVariantMessage("Variant enregistre.");
  };

  const handleVariantEdit = (variant) => {
    setVariantForm({
      id: variant.id,
      product_id: variant.product_id,
      sku: variant.sku,
      name: variant.name,
      size_label: variant.size_label || "",
      weight_value: variant.weight_value ?? "",
      weight_unit: variant.weight_unit || "g",
      price_override: variant.price_override ?? "",
      is_default: variant.is_default,
    });
    setVariantMessage("");
  };

  const handleVariantDelete = async (id) => {
    await deleteProductVariant(id);
    const refreshedVariants = await loadVariantsForProduct(selectedVariantProductId);
    setVariants(refreshedVariants);
    if (variantForm.id === id) {
      resetVariantForm();
    }
    setVariantMessage("Variant supprime.");
  };

  const resetMenuForm = () => {
    setMenuForm(emptyMenuForm);
    setEditingMenuId(null);
    setMenuMessage("");
  };

  const handleMenuChange = (event) => {
    const { name, value } = event.target;
    setMenuForm((current) => ({ ...current, [name]: value }));
  };

  const handleMenuSubmit = async (event) => {
    event.preventDefault();
    if (!menuForm.name.trim() || Number(menuForm.price) <= 0) {
      setMenuMessage("Nom et prix sont requis.");
      return;
    }
    const components = menuForm.componentsText
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);
    await saveMenu({
      id: editingMenuId || undefined,
      name: menuForm.name.trim(),
      price: Number(menuForm.price),
      businessUnit: "traiteur",
      components,
      sortOrder: menuForm.sortOrder,
    });
    setMenus(await loadMenus());
    resetMenuForm();
    setMenuMessage("Menu enregistre.");
  };

  const handleMenuEdit = (menu) => {
    setMenuForm({
      id: menu.id,
      name: menu.name,
      price: menu.price,
      businessUnit: "traiteur",
      componentsText: (menu.components || []).join("\n"),
      sortOrder: menu.sortOrder ?? 0,
    });
    setEditingMenuId(menu.id);
    setMenuMessage("");
  };

  const handleMenuDelete = async (id) => {
    await deleteMenu(id);
    setMenus(await loadMenus());
    if (editingMenuId === id) {
      resetMenuForm();
    }
    setMenuMessage("Menu supprime.");
  };

  const handleDelete = async (id) => {
    const saved = await saveCatalog(
      products.filter((product) => product.id !== id)
    );
    setProducts(saved);
    if (editingId === id) {
      resetForm();
    }
    setProductMessage("Produit supprime.");
  };

  const handleGenerateBarcode = () => {
    setForm((current) => ({
      ...current,
      barcode: generateInternalBarcode(
        products
          .filter((product) => product.id !== editingId)
          .map((product) => product.barcode)
      ),
    }));
    setProductMessage("Code-barres interne genere.");
    setProductError("");
  };

  useEffect(() => {
    setProductPage(1);
  }, [productSearch]);

  useEffect(() => {
    if (productPage > totalProductPages) {
      setProductPage(totalProductPages);
    }
  }, [productPage, totalProductPages]);

  useEffect(() => {
    const value = String(form.barcode || "").trim();
    if (!value || !barcodePreviewRef.current) {
      return;
    }
    try {
      barcodePreviewRef.current.innerHTML = "";
      JsBarcode(barcodePreviewRef.current, value, {
        format: barcodeFormatFor(value),
        width: 1.4,
        height: 34,
        displayValue: true,
        fontSize: 10,
        margin: 0,
      });
    } catch (err) {
      barcodePreviewRef.current.innerHTML = "";
    }
  }, [form.barcode]);

  const handlePrintNewProduct = () => {
    if (recentlyCreated) {
      printProductLabel(recentlyCreated);
      setRecentlyCreated(null);
    }
  };

  useEffect(() => {
    if (!recentlyCreated || !recentlyCreatedPreviewRef.current) return;
    const value = String(recentlyCreated.barcode || "").trim();
    if (!value) return;
    try {
      recentlyCreatedPreviewRef.current.innerHTML = "";
      JsBarcode(recentlyCreatedPreviewRef.current, value, {
        format: barcodeFormatFor(value),
        width: 1,
        height: 38,
        displayValue: true,
        fontSize: 8,
        margin: 0,
      });
    } catch (err) {
      recentlyCreatedPreviewRef.current.innerHTML = "";
    }
  }, [recentlyCreated]);

  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const text = await file.text();
    const { imported, skipped } = importPrestashopProductCsv(text, products);
    const saved = await saveCatalog(imported);
    setProducts(saved);
    setImportMessage(
      `${saved.length} produits disponibles apres import. ${skipped} ligne(s) ignoree(s).`
    );
    event.target.value = "";
  };

  return (
    <div>
      <ScrollToTop />
      <section className="admin-section">
        <div className="container">
          <div className="row">
            <AdminSidebar activePath="/MyAccountAddress" />

            <div className="col-lg-9 col-md-8 col-12">
              {loaderStatus ? (
                <div className="p-6 p-lg-10">
                  <div className="alert alert-light border mb-0">
                    Chargement du catalogue...
                  </div>
                </div>
              ) : (
                <div className="p-6 p-lg-10">
                  <div className="d-flex justify-content-between mb-6 flex-wrap gap-2">
                    <div>
                      <h2 className="mb-1">Catalogue</h2>
                      <p className="text-muted mb-0">Ajout, edition et stock des produits directement dans l'application.</p>
                    </div>
                    <button className="btn btn-outline-primary" type="button" onClick={() => { resetForm(); setActiveTab("ajouter"); setProductFormTab("informations"); }}>
                      Nouveau produit
                    </button>
                  </div>

                  <div className="admin-tabs">
                    {[
                      { key: "produits", label: "Produits" },
                      { key: "ajouter", label: "Ajouter" },
                      { key: "categories", label: "Categories" },
                      { key: "menues", label: "Menues" },
                      { key: "coupons", label: "Coupons" },
                      { key: "giftcards", label: "Cartes cadeaux" },
                      { key: "loyalty", label: "Fidelite" },
                      { key: "import", label: "Import CSV" },
                    ].map((tab) => (
                      <button
                        key={tab.key}
                        type="button"
                        className={`admin-tab-btn${activeTab === tab.key ? " active" : ""}`}
                        onClick={() => setActiveTab(tab.key)}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  {(activeTab === "produits" || activeTab === "ajouter") && (
                    <div className="row g-4 mb-8">
                      <div className="col-md-4">
                        <div className="card border-0 shadow-sm h-100">
                          <div className="card-body p-4">
                            <p className="text-muted mb-2">Produits enregistres</p>
                            <h3 className="mb-0">{products.length}</h3>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="card border-0 shadow-sm h-100">
                          <div className="card-body p-4">
                            <p className="text-muted mb-2">Produits mis en avant</p>
                            <h3 className="mb-0">{featuredCount}</h3>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="card border-0 shadow-sm h-100">
                          <div className="card-body p-4">
                            <p className="text-muted mb-2">Stock faible</p>
                            <h3 className="mb-0">{lowStockCount}</h3>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === "import" && (
                  <div className="card border-0 shadow-sm mb-8">
                    <div className="card-body p-4">
                      <h5 className="mb-3">Importer un export produit PrestaShop</h5>
                      <p className="text-muted mb-3">
                        Utilisez le fichier type <code>product_2025-07-15_161526.csv</code> pour recuperer nom, categorie, prix et stock.
                      </p>
                      <input
                        type="file"
                        accept=".csv"
                        className="form-control"
                        onChange={handleImportFile}
                      />
                      {importMessage ? (
                        <div className="alert alert-success mt-3 mb-0" role="alert">
                          {importMessage}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  )}

                  {activeTab === "ajouter" && (
                  <div className="card border-0 shadow-sm mb-8" ref={productFormCardRef}>
                    <div className="card-body p-4">
                      <h5 className="mb-4">{editingId ? "Modifier le produit" : "Ajouter un produit"}</h5>
                      {productError ? (
                        <div className="alert alert-danger mb-4">{productError}</div>
                      ) : null}
                      {productMessage ? (
                        <div className="alert alert-success mb-4">{productMessage}</div>
                      ) : null}
                      {recentlyCreated ? (
                        <div className="card border border-success mb-4" style={{ background: "#f8fdf9" }}>
                          <div className="card-body d-flex flex-wrap align-items-center gap-3">
                            <div className="flex-grow-1" style={{ minWidth: "180px" }}>
                              <div className="small fw-semibold text-success mb-1">
                                <i className="bi bi-check-circle-fill me-1"></i>Produit enregistre. Imprimez l'etiquette :
                              </div>
                              <div className="fw-bold">{recentlyCreated.name}</div>
                              <div className="small text-muted">Code-barres : {recentlyCreated.barcode || "aucun"}</div>
                            </div>
                            <div className="me-2">
                              <svg ref={recentlyCreatedPreviewRef} style={{ height: 45, maxWidth: 220 }}></svg>
                            </div>
                            <button className="btn btn-dark btn-sm" type="button" onClick={handlePrintNewProduct}>
                              <i className="bi bi-printer me-1"></i>Imprimer l'etiquette
                            </button>
                            <button className="btn btn-outline-secondary btn-sm" type="button" onClick={() => setRecentlyCreated(null)}>
                              Fermer
                            </button>
                          </div>
                        </div>
                      ) : null}
                      <div className="d-flex justify-content-center mb-4 pb-3 border-bottom">
                          <div className="btn-group shadow-sm" role="group">
                            <input type="radio" className="btn-check" name="businessUnitSwitcher" id="unitShop" autoComplete="off" checked={form.businessUnit !== 'traiteur'} onChange={() => setForm({...form, businessUnit: 'alimentation'})} />
                            <label className="btn btn-outline-primary px-5 fw-bold" htmlFor="unitShop"><i className="bi bi-shop me-2"></i>Alimentation (SHOP)</label>
                            
                            <input type="radio" className="btn-check" name="businessUnitSwitcher" id="unitTraiteur" autoComplete="off" checked={form.businessUnit === 'traiteur'} onChange={() => setForm({...form, businessUnit: 'traiteur'})} />
                            <label className="btn btn-outline-warning px-5 fw-bold" htmlFor="unitTraiteur"><i className="bi bi-egg-fried me-2"></i>Traiteur</label>
                          </div>
                        </div>
                        <form onSubmit={handleSubmit}>
                        <div className="row">
                          {/* Sidebar Tabs */}
                          <div className="col-md-3">
                            <div className="nav flex-column nav-pills" role="tablist" aria-orientation="vertical">
                              <button className={`nav-link text-start mb-2 ${productFormTab === 'informations' ? 'active' : 'bg-light text-dark'}`} type="button" onClick={() => setProductFormTab('informations')}>Informations</button>
                              <button className={`nav-link text-start mb-2 ${productFormTab === 'prix' ? 'active' : 'bg-light text-dark'}`} type="button" onClick={() => setProductFormTab('prix')}>Prix</button>
                              <button className={`nav-link text-start mb-2 ${productFormTab === 'prix-specifiques' ? 'active' : 'bg-light text-dark'}`} type="button" onClick={() => setProductFormTab('prix-specifiques')}>Prix spécifiques</button>
                              <button className={`nav-link text-start mb-2 ${productFormTab === 'categories' ? 'active' : 'bg-light text-dark'}`} type="button" onClick={() => setProductFormTab('categories')}>Catégories</button>
                              <button className={`nav-link text-start mb-2 ${productFormTab === 'livraison' ? 'active' : 'bg-light text-dark'}`} type="button" onClick={() => setProductFormTab('livraison')}>Livraison</button>
                              <button className={`nav-link text-start mb-2 ${productFormTab === 'quantites' ? 'active' : 'bg-light text-dark'}`} type="button" onClick={() => setProductFormTab('quantites')}>Quantités</button>
                              <button className={`nav-link text-start mb-2 ${productFormTab === 'images' ? 'active' : 'bg-light text-dark'}`} type="button" onClick={() => setProductFormTab('images')}>Images</button>
                              <button className={`nav-link text-start mb-2 ${productFormTab === 'fournisseurs' ? 'active' : 'bg-light text-dark'}`} type="button" onClick={() => setProductFormTab('fournisseurs')}>Fournisseurs</button>
                            </div>
                          </div>
                          
                          {/* Main Content */}
                          <div className="col-md-9">
                            <div className="card shadow-sm border-0" style={{ backgroundColor: "#fbfbfb" }}>
                              <div className="card-body p-4">
                                <h6 className="mb-4 fw-bold text-uppercase text-secondary border-bottom pb-2">{productFormTab}</h6>
                                
                                {productFormTab === 'informations' && (
                                  <div className="row g-3">
                                    <div className="col-lg-6">
                                      <label className="form-label">Nom</label>
                                      <input ref={productNameInputRef} name="name" className="form-control" value={form.name} onChange={handleChange} required />
                                    </div>
                                    <div className="col-lg-6">
                                      <label className="form-label">Unité</label>
                                      <select name="businessUnit" className="form-select" value={form.businessUnit || "alimentation"} onChange={handleChange}>
                                        <option value="alimentation">Alimentation (SHOP)</option>
                                        <option value="traiteur">Traiteur</option>
                                      </select>
                                    </div>
                                    <div className="col-lg-4">
                                      <label className="form-label">Type produit</label>
                                      <select name="type" className="form-select" value={form.type} onChange={handleChange}>
                                        <option value="regular">Regular</option>
                                        <option value="fresh">Fresh</option>
                                        <option value="frozen">Frozen</option>
                                      </select>
                                    </div>
                                    <div className="col-lg-4">
                                      <label className="form-label">Mode de vente</label>
                                      <select name="saleType" className="form-select" value={form.saleType} onChange={handleChange}>
                                        <option value="unit">Par unite</option>
                                        <option value="weight">Par poids</option>
                                        <option value="cut_to_weight">Coupe + pesage</option>
                                      </select>
                                    </div>
                                    <div className="col-lg-4">
                                      <label className="form-label">Unite</label>
                                      <select name="unitType" className="form-select" value={form.unitType} onChange={handleChange}>
                                        <option value="piece">Piece</option>
                                        <option value="kg">Kg</option>
                                        <option value="g">g</option>
                                        <option value="l">L</option>
                                      </select>
                                    </div>
                                    <div className="col-lg-12">
                                      <label className="form-label">Description du produit</label>
                                      <textarea
                                        name="description"
                                        className="form-control"
                                        rows="3"
                                        value={form.description}
                                        onChange={handleChange}
                                        placeholder="Description courte du produit pour la fiche client..."
                                      />
                                    </div>
                                    <div className="col-lg-4">
                                      <label className="form-label">Ordre d'affichage</label>
                                      <input type="number" name="sortOrder" className="form-control" value={form.sortOrder} onChange={handleChange} placeholder="0" />
                                      <small className="text-muted">Plus petit = affiche en premier</small>
                                    </div>
                                    <div className="col-12 mt-2">
                                      <div className="form-check">
                                        <input id="featuredProduct" name="featured" type="checkbox" className="form-check-input" checked={form.featured} onChange={handleChange} />
                                        <label className="form-check-label fw-bold" htmlFor="featuredProduct">Mettre ce produit en avant sur le site client</label>
                                      </div>
                                    </div>
                                    <div className="col-12 mt-3 pt-3 border-top">
                                      <h6 className="mb-3 text-muted">Déclinaison / Taille</h6>
                                      <div className="row g-3">
                                        <div className="col-lg-4">
                                          <label className="form-label">Variant / taille</label>
                                          <input name="variantLabel" className="form-control" value={form.variantLabel} onChange={handleChange} placeholder="500g, 1kg, boite..." />
                                        </div>
                                        <div className="col-lg-4">
                                          <label className="form-label">Poids variant</label>
                                          <input name="variantWeightValue" type="number" step="0.001" className="form-control" value={form.variantWeightValue} onChange={handleChange} placeholder="0.500 ou 500" />
                                        </div>
                                        <div className="col-lg-4">
                                          <label className="form-label">Unite poids</label>
                                          <select name="variantWeightUnit" className="form-select" value={form.variantWeightUnit} onChange={handleChange}>
                                            <option value="g">g</option>
                                            <option value="kg">kg</option>
                                          </select>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                                
                                {productFormTab === 'prix' && (
                                  <div className="row g-3">
                                    <div className="col-12">
                                      <h6 className="mb-3">PRIX DU PRODUIT</h6>
                                    </div>
                                    <div className="col-lg-6">
                                      <label className="form-label">Prix d'achat HT</label>
                                      <div className="input-group">
                                        <input name="purchasePriceHT" type="number" step="0.01" className="form-control" value={form.purchasePriceHT || form.purchasePrice || ""} onChange={(e) => setForm({ ...form, purchasePriceHT: e.target.value, purchasePrice: e.target.value })} />
                                        <span className="input-group-text">€</span>
                                      </div>
                                    </div>
                                    <div className="col-lg-6">
                                      <label className="form-label fw-bold">Prix de vente HT</label>
                                      <div className="input-group">
                                        <input name="priceHT" type="number" step="0.01" className="form-control" value={form.priceHT || form.price || ""} onChange={(e) => setForm({ ...form, priceHT: e.target.value, price: e.target.value })} required />
                                        <span className="input-group-text">€</span>
                                      </div>
                                    </div>
                                    <div className="col-lg-6">
                                      <label className="form-label">Règle de taxe (Taux TVA)</label>
                                      <select
                                        className="form-select"
                                        value={form.tax_rate_id || ""}
                                        onChange={(e) => setForm({ ...form, tax_rate_id: e.target.value })}
                                      >
                                        <option value="">-- Aucune taxe --</option>
                                        {taxRates.filter((t) => t.is_active).map((t) => (
                                          <option key={t.id} value={t.id}>{t.label} ({t.rate}%)</option>
                                        ))}
                                      </select>
                                    </div>
                                    <div className="col-lg-6">
                                      <label className="form-label fw-bold">Prix de vente TTC</label>
                                      <div className="input-group">
                                        <input 
                                          name="priceTTC" 
                                          type="number" 
                                          step="0.01" 
                                          className="form-control" 
                                          value={form.priceTTC || ((form.priceHT || form.price) && form.tax_rate_id ? computeTTC(form.priceHT || form.price, taxRates.find(t => t.id === form.tax_rate_id)?.rate || 0).toFixed(2) : (form.priceHT || form.price || ""))} 
                                          onChange={handleChange} 
                                        />
                                        <span className="input-group-text">€</span>
                                      </div>
                                    </div>
                                    
                                    <div className="col-lg-12">
                                      <label className="form-label fw-bold">Prix unitaire HT</label>
                                      <div className="input-group mb-3">
                                        <input name="unitPriceHT" type="number" step="0.01" className="form-control" value={form.unitPriceHT || ""} onChange={handleChange} placeholder="0.00" />
                                        <span className="input-group-text">€ par</span>
                                        <input name="unitPriceUnit" type="text" className="form-control" value={form.unitPriceUnit || ""} onChange={handleChange} placeholder="kg, litre, unité..." />
                                      </div>
                                    </div>
                                    
                                    <div className="col-12 mt-1 mb-3">
                                      <div className="form-check form-switch">
                                        <input className="form-check-input" type="checkbox" id="onSaleCheckbox" name="onSale" checked={!!form.onSale} onChange={(e) => setForm({...form, onSale: e.target.checked})} />
                                        <label className="form-check-label fw-bold text-danger" htmlFor="onSaleCheckbox">
                                          Afficher un bandeau "Promo!"
                                        </label>
                                      </div>
                                    </div>
                                    
                                    <div className="col-12 mt-4 pt-3 border-top">
                                      <h6 className="text-success mb-3">🌿 Anti-gaspi</h6>
                                      <div className="row g-3">
                                        <div className="col-lg-3">
                                          <label className="form-label">Reduction (%)</label>
                                          <input name="antiGaspiDiscount" type="number" min="0" max="90" className="form-control" value={form.antiGaspiDiscount} onChange={handleChange} placeholder="Ex: 30" />
                                        </div>
                                        <div className="col-lg-3">
                                          <label className="form-label">Quantite max remisee</label>
                                          <input name="antiGaspiMaxQty" type="number" min="1" className="form-control" value={form.antiGaspiMaxQty} onChange={handleChange} placeholder="Ex: 5 (vide = illimite)" />
                                        </div>
                                        <div className="col-lg-3">
                                          <label className="form-label">Du</label>
                                          <input name="antiGaspiStartsAt" type="date" className="form-control" value={form.antiGaspiStartsAt} onChange={handleChange} />
                                        </div>
                                        <div className="col-lg-3">
                                          <label className="form-label">Au</label>
                                          <input name="antiGaspiExpiresAt" type="date" className="form-control" value={form.antiGaspiExpiresAt} onChange={handleChange} />
                                        </div>
                                        <div className="col-lg-4">
                                          <label className="form-label">Motif</label>
                                          <select name="antiGaspiReason" className="form-select" value={form.antiGaspiReason} onChange={handleChange}>
                                            <option value="">Aucun</option>
                                            <option value="Date courte">Date courte</option>
                                            <option value="Emballage abime">Emballage abime</option>
                                            <option value="Fond de lot">Fond de lot</option>
                                          </select>
                                        </div>
                                      </div>
                                      <small className="text-muted d-block mt-2">Laissez la reduction vide pour desactiver l'anti-gaspi sur ce produit.</small>
                                    </div>
                                  </div>
                                )}
                                
                                {productFormTab === 'prix-specifiques' && (
                                  <div className="row g-3">
                                    <div className="col-12">
                                      <h6 className="mb-3">PRIX SPÉCIFIQUES</h6>
                                      <div className="alert alert-info border mb-3">
                                        Vous pouvez définir des prix spécifiques pour des clients appartenant à différents groupes, différents pays, etc.
                                      </div>
                                      
                                      <div className="border p-3 mb-4 rounded bg-light">
                                        <div className="row g-3">
                                          <div className="col-md-4">
                                            <label className="form-label fw-bold">Pour (Groupe)</label>
                                            <select 
                                              className="form-select" 
                                              id="new_specific_price_group"
                                            >
                                              <option value="Tous les groupes">Tous les groupes</option>
                                              <option value="Boutique">Boutique</option>
                                              <option value="en magasin">En magasin</option>
                                              <option value="a emporter">À emporter</option>
                                              <option value="a livrer">À livrer</option>
                                              {customerGroups.map(g => (
                                                <option key={g.id} value={g.name}>{g.name}</option>
                                              ))}
                                            </select>
                                          </div>
                                          <div className="col-md-8">
                                            <label className="form-label fw-bold">Client</label>
                                            <div className="input-group">
                                              <input type="text" className="form-control" placeholder="Tous les clients" disabled />
                                              <button className="btn btn-outline-secondary" type="button"><i className="bi bi-search"></i></button>
                                            </div>
                                          </div>
                                          <div className="col-md-6">
                                            <label className="form-label fw-bold">Disponible (de)</label>
                                            <input type="date" className="form-control" id="new_specific_price_from" />
                                          </div>
                                          <div className="col-md-6">
                                            <label className="form-label fw-bold">Disponible (jusqu'au)</label>
                                            <input type="date" className="form-control" id="new_specific_price_to" />
                                          </div>
                                          <div className="col-md-4">
                                            <label className="form-label fw-bold">À partir de (unité(s))</label>
                                            <input type="number" min="1" defaultValue="1" className="form-control" id="new_specific_price_qty" />
                                          </div>
                                          <div className="col-md-8">
                                            <label className="form-label fw-bold">Prix du produit (HT)</label>
                                            <div className="input-group mb-2">
                                              <span className="input-group-text">€</span>
                                              <input type="number" step="0.01" className="form-control" id="new_specific_price_ht" defaultValue={form.price || ""} />
                                            </div>
                                            <div className="form-check">
                                              <input className="form-check-input" type="checkbox" id="new_specific_price_leave_base" defaultChecked />
                                              <label className="form-check-label" htmlFor="new_specific_price_leave_base">
                                                Laisser le prix de base
                                              </label>
                                            </div>
                                          </div>
                                          <div className="col-md-12">
                                            <label className="form-label fw-bold">Appliquer une réduction de</label>
                                            <div className="input-group">
                                              <input type="number" step="0.01" defaultValue="0.00" className="form-control" id="new_specific_price_discount" />
                                              <select className="form-select" style={{maxWidth: "100px"}} id="new_specific_price_discount_type">
                                                <option value="amount">Euro</option>
                                                <option value="percent">%</option>
                                              </select>
                                              <select className="form-select" style={{maxWidth: "100px"}} id="new_specific_price_discount_tax">
                                                <option value="TTC">TTC</option>
                                                <option value="HT">HT</option>
                                              </select>
                                            </div>
                                          </div>
                                          <div className="col-12 mt-3 text-end">
                                            <button 
                                              type="button" 
                                              className="btn btn-primary btn-sm"
                                              onClick={() => {
                                                const group = document.getElementById('new_specific_price_group').value;
                                                const fromDate = document.getElementById('new_specific_price_from').value;
                                                const toDate = document.getElementById('new_specific_price_to').value;
                                                const qty = document.getElementById('new_specific_price_qty').value;
                                                const priceHT = document.getElementById('new_specific_price_ht').value;
                                                const leaveBase = document.getElementById('new_specific_price_leave_base').checked;
                                                const discount = document.getElementById('new_specific_price_discount').value;
                                                const discountType = document.getElementById('new_specific_price_discount_type').value;
                                                
                                                const newPrice = {
                                                  id: Date.now().toString(),
                                                  group,
                                                  client: 'Tous les clients',
                                                  fromDate,
                                                  toDate,
                                                  qty,
                                                  priceHT: leaveBase ? (form.price || 0) : Number(priceHT),
                                                  leaveBase,
                                                  discount: Number(discount),
                                                  discountType
                                                };
                                                
                                                const currentPrices = form.specificPrices || [];
                                                setForm({ ...form, specificPrices: [...currentPrices, newPrice] });
                                              }}
                                            >
                                              <i className="bi bi-plus-lg me-1"></i>Ajouter un nouveau prix spécifique
                                            </button>
                                          </div>
                                        </div>
                                      </div>

                                      <div className="table-responsive">
                                        <table className="table table-sm table-bordered">
                                          <thead className="table-light">
                                            <tr>
                                              <th>Règle</th>
                                              <th>Déclinaison</th>
                                              <th>Devise</th>
                                              <th>Pays</th>
                                              <th>Groupe</th>
                                              <th>Client</th>
                                              <th>Prix du produit HT</th>
                                              <th>Impact</th>
                                              <th>Période</th>
                                              <th>À partir de (quantité)</th>
                                              <th>Outils</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {form.specificPrices && form.specificPrices.map((sp, idx) => (
                                              <tr key={sp.id || idx}>
                                                <td>--</td>
                                                <td>Toutes les déclinaisons</td>
                                                <td>Toutes les devises</td>
                                                <td>Tous les pays</td>
                                                <td>{sp.group}</td>
                                                <td>{sp.client}</td>
                                                <td>{sp.leaveBase ? '--' : `${sp.priceHT} €`}</td>
                                                <td>{sp.discount > 0 ? (sp.discountType === 'percent' ? `-${sp.discount}%` : `-${sp.discount} €`) : '--'}</td>
                                                <td>{(sp.fromDate || sp.toDate) ? `${sp.fromDate} à ${sp.toDate}` : 'Illimité'}</td>
                                                <td>{sp.qty}</td>
                                                <td>
                                                  <button 
                                                    className="btn btn-sm btn-outline-danger" 
                                                    type="button"
                                                    onClick={() => {
                                                      const currentPrices = form.specificPrices || [];
                                                      setForm({ ...form, specificPrices: currentPrices.filter(p => p.id !== sp.id) });
                                                    }}
                                                  >
                                                    <i className="bi bi-trash"></i>
                                                  </button>
                                                </td>
                                              </tr>
                                            ))}
                                            {(!form.specificPrices || form.specificPrices.length === 0) && (
                                              <tr>
                                                <td colSpan="11" className="text-center text-muted py-3">Aucun prix spécifique défini</td>
                                              </tr>
                                            )}
                                          </tbody>
                                        </table>
                                      </div>

                                    </div>
                                  </div>
                                )}

                                {productFormTab === 'categories' && (
                                  <div className="row g-3">
                                    <div className="col-12">
                                      <p className="text-muted mb-2">Sélectionnez les catégories dans lesquelles ce produit apparaîtra :</p>
                                      <div className="border rounded-3 p-3 bg-white" style={{ maxHeight: "350px", overflowY: "auto" }}>
                                        {(form.businessUnit === "traiteur" ? traiteurCategoryTree : categoryTree).map((top) => (
                                          <div key={top.slug} className="mb-3 border-bottom pb-2">
                                            <div className="form-check mb-2">
                                              <input
                                                className="form-check-input fw-bold"
                                                type="checkbox"
                                                id={`cat-${top.slug}`}
                                                checked={(Array.isArray(form.category) ? form.category : []).includes(top.name)}
                                                onChange={() => handleProductCategoryToggle(top.name)}
                                              />
                                              <label className="form-check-label fw-bold text-uppercase" htmlFor={`cat-${top.slug}`}>{top.name}</label>
                                            </div>
                                            {top.children.map((child) => (
                                              <div key={child.slug} className="ms-4 mb-1">
                                                <div className="form-check">
                                                  <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id={`cat-${child.slug}`}
                                                    checked={(Array.isArray(form.category) ? form.category : []).includes(child.name)}
                                                    onChange={() => handleProductCategoryToggle(child.name)}
                                                  />
                                                  <label className="form-check-label fw-semibold text-primary" htmlFor={`cat-${child.slug}`}>{child.name}</label>
                                                </div>
                                                {child.leaves.filter(leaf => leaf !== child.name).map(leaf => (
                                                  <div key={leaf} className="ms-4">
                                                    <div className="form-check">
                                                      <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        id={`cat-leaf-${leaf.replace(/\s+/g, "-")}`}
                                                        checked={(Array.isArray(form.category) ? form.category : []).includes(leaf)}
                                                        onChange={() => handleProductCategoryToggle(leaf)}
                                                      />
                                                      <label className="form-check-label text-muted" htmlFor={`cat-leaf-${leaf.replace(/\s+/g, "-")}`}>{leaf}</label>
                                                    </div>
                                                  </div>
                                                ))}
                                              </div>
                                            ))}
                                          </div>
                                        ))}
                                        {!(form.businessUnit === "traiteur" ? traiteurCategoryTree : categoryTree).length ? (
                                          <small className="text-muted">Aucune categorie disponible.</small>
                                        ) : null}
                                      </div>
                                    </div>
                                  </div>
                                )}
                                
                                {productFormTab === 'livraison' && (
                                  <div className="row g-3">
                                    <div className="col-12">
                                      <div className="alert alert-info border mb-3">
                                        <strong>Regle de livraison applicable à ce produit :</strong><br/> {deliveryRules[form.type]}
                                      </div>
                                    </div>
                                    <div className="col-lg-6">
                                      <label className="form-label fw-bold">Max par commande</label>
                                      <input name="maxPerOrder" type="number" min="1" className="form-control" value={form.maxPerOrder} onChange={handleChange} placeholder="Ex: 4 (Laissez vide pour aucune limite)" />
                                      <small className="text-muted">Limiter le nombre d'unités qu'un client peut acheter.</small>
                                    </div>
                                    <div className="col-lg-12 mt-4">
                                      <label className="form-label fw-bold">Zones autorisees</label>
                                      <div className="border rounded-3 p-3 bg-white" style={{ maxHeight: "200px", overflowY: "auto" }}>
                                        {deliveryZones.length ? (
                                          deliveryZones.map((zone) => (
                                            <div className="form-check" key={zone.id}>
                                              <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id={`zone-${zone.id}`}
                                                checked={form.allowedZoneIds.includes(zone.id)}
                                                onChange={() => handleAllowedZoneToggle(zone.id)}
                                              />
                                              <label className="form-check-label" htmlFor={`zone-${zone.id}`}>
                                                {zone.name}
                                              </label>
                                            </div>
                                          ))
                                        ) : (
                                          <small className="text-muted">Aucune zone configuree pour le moment.</small>
                                        )}
                                      </div>
                                      <div className="alert alert-warning mt-2 py-2 mb-0">
                                        <i className="bi bi-exclamation-circle me-2"></i>
                                        Laissez vide pour autoriser <strong>toutes</strong> les zones compatibles.
                                      </div>
                                    </div>
                                  </div>
                                )}
                                
                                {productFormTab === 'quantites' && (
                                  <div className="row g-3">
                                    <div className="col-lg-6">
                                      <label className="form-label fw-bold">Stock actuel</label>
                                      <input name="stock" type="number" className="form-control form-control-lg" value={form.stock} onChange={handleChange} required />
                                    </div>
                                    
                                    {form.saleType !== "unit" ? (
                                      <div className="col-12 mt-4 pt-3 border-top">
                                        <h6 className="mb-3 text-muted">Paramètres de pesage</h6>
                                        <div className="row g-3">
                                          <div className="col-lg-4">
                                            <label className="form-label">Poids minimum (kg)</label>
                                            <input name="minWeightKg" type="number" step="0.001" className="form-control" value={form.minWeightKg} onChange={handleChange} placeholder="0.250" />
                                          </div>
                                          <div className="col-lg-4">
                                            <label className="form-label">Palier (kg)</label>
                                            <input name="stepWeightKg" type="number" step="0.001" className="form-control" value={form.stepWeightKg} onChange={handleChange} placeholder="0.050" />
                                          </div>
                                          <div className="col-lg-4">
                                            <label className="form-label">Poids approx. (kg)</label>
                                            <input name="approximateWeightKg" type="number" step="0.001" className="form-control" value={form.approximateWeightKg} onChange={handleChange} placeholder="0.500" />
                                          </div>
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="col-12 mt-3">
                                        <div className="alert alert-light border text-muted">
                                          Ce produit est vendu à l'unité. Les paramètres de pesage ne sont pas applicables.
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                                
                                {productFormTab === 'images' && (
                                  <div className="row g-3">
                                    <div className="col-lg-12">
                                      <label className="form-label fw-bold">Image du produit</label>
                                      <div className="d-flex align-items-center gap-4 bg-white p-3 border rounded-3">
                                        {form.imageUrl ? (
                                          <div className="border rounded bg-light p-1" style={{ width: "120px", height: "120px", display: "flex", justifyContent: "center", alignItems: "center" }}>
                                            <img src={form.imageUrl} alt="Apercu" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
                                          </div>
                                        ) : (
                                          <div className="border rounded bg-light p-1 text-muted d-flex align-items-center justify-content-center" style={{ width: "120px", height: "120px" }}>
                                            <i className="bi bi-image" style={{ fontSize: "2rem" }}></i>
                                          </div>
                                        )}
                                        
                                        <div className="flex-grow-1">
                                          <input
                                            type="file"
                                            accept="image/*"
                                            className="form-control mb-3"
                                            onChange={async (e) => {
                                              const file = e.target.files?.[0];
                                              if (!file) return;
                                              const url = await uploadProductImage(file);
                                              setForm({ ...form, imageUrl: url });
                                            }}
                                          />
                                          <input name="imageUrl" className="form-control" value={form.imageUrl} onChange={handleChange} placeholder="Ou saisissez une URL d'image existante..." />
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                                
                                {productFormTab === 'fournisseurs' && (
                                  <div className="row g-3">
                                    <div className="col-lg-6">
                                      <label className="form-label fw-bold">Fournisseur</label>
                                      <select
                                        className="form-select"
                                        value={form.supplierId || ""}
                                        onChange={(e) => setForm({ ...form, supplierId: e.target.value })}
                                      >
                                        <option value="">-- Sans fournisseur --</option>
                                        {suppliers.filter((s) => s.active).map((s) => (
                                          <option key={s.id} value={s.id}>{s.name}</option>
                                        ))}
                                      </select>
                                    </div>
                                    <div className="col-lg-6">
                                      <label className="form-label fw-bold">SKU (Référence interne)</label>
                                      <input name="sku" className="form-control" value={form.sku} onChange={handleChange} required />
                                    </div>
                                    <div className="col-lg-12 mt-4">
                                      <label className="form-label fw-bold">Code-barres (EAN-13 ou interne)</label>
                                      <div className="input-group">
                                        <span className="input-group-text"><i className="bi bi-upc-scan"></i></span>
                                        <input
                                          name="barcode"
                                          className="form-control form-control-lg"
                                          value={form.barcode}
                                          onChange={handleChange}
                                          placeholder="Scannez ou genere..."
                                        />
                                        <button
                                          className="btn btn-outline-dark"
                                          type="button"
                                          onClick={handleGenerateBarcode}
                                        >
                                          Generer
                                        </button>
                                      </div>
                                      <small className="text-muted d-block mt-2">Le scanner-gun de caisse peut ecrire directement dans ce champ.</small>
                                      
                                      {form.barcode ? (
                                        <div className="mt-4 p-3 bg-white border rounded text-center" style={{ maxWidth: "300px" }}>
                                          <svg ref={barcodePreviewRef} style={{ height: 60, maxWidth: "100%" }}></svg>
                                          <div className="small fw-bold mt-1" style={{ letterSpacing: "2px" }}>{form.barcode}</div>
                                        </div>
                                      ) : null}
                                    </div>
                                  </div>
                                )}
                                
                              </div>
                            </div>
                          </div>
                          
                          {/* Action Buttons */}
                          <div className="col-12 mt-4 pt-3 border-top d-flex gap-2 justify-content-end">
                            <button className="btn btn-outline-dark px-4" type="button" onClick={() => {
                                resetForm();
                                setActiveTab("produits");
                            }}>
                              Annuler
                            </button>
                            <button className="btn btn-primary px-4" type="submit">
                              {editingId ? "Enregistrer" : "Enregistrer"}
                            </button>
                          </div>
                        </div>
                      </form>
                    </div>
                  </div>
                  )}

                  {activeTab === "categories" && (
                  <>
                    <div className="card border-0 shadow-sm mb-8">
                      <div className="card-body p-4">
                        <h5 className="mb-4">Categories principales</h5>
                        <div className="row g-4">
                          {categorySummary.map((category) => (
                            <div className="col-lg-6" key={category.name}>
                              <div className="border rounded-3 p-4 h-100">
                                <div className="d-flex justify-content-between align-items-start">
                                  <div>
                                    <h6 className="mb-1">{category.name}</h6>
                                    <p className="text-muted mb-0">{category.count} produits</p>
                                  </div>
                                  <span className="badge bg-success">{category.status}</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="row g-4 mb-8">
                      <div className="col-lg-5">
                        <div className="card border-0 shadow-sm h-100">
                          <div className="card-body p-4">
                            <div className="d-flex justify-content-between align-items-center mb-4">
                              <h5 className="mb-0">Categories</h5>
                              <button className="btn btn-outline-dark btn-sm" type="button" onClick={resetCategoryForm}>
                                Nouvelle
                              </button>
                            </div>
                            <form onSubmit={handleCategorySubmit}>
                              <div className="row g-3">
                                <div className="col-12">
                                  <label className="form-label">Nom</label>
                                  <input name="name" className="form-control" value={categoryForm.name} onChange={handleCategoryChange} required />
                                </div>
                                <div className="col-12">
                                  <label className="form-label">Description</label>
                                  <textarea name="description" className="form-control" rows="3" value={categoryForm.description} onChange={handleCategoryChange} />
                                </div>
                                <div className="col-12">
                                  <label className="form-label">Ordre</label>
                                  <input name="sort_order" type="number" className="form-control" value={categoryForm.sort_order} onChange={handleCategoryChange} />
                                </div>
                                {categoryMessage ? (
                                  <div className="col-12">
                                    <div className="alert alert-success mb-0">{categoryMessage}</div>
                                  </div>
                                ) : null}
                                <div className="col-12 d-flex gap-2">
                                  <button className="btn btn-primary" type="submit">
                                    {categoryForm.id ? "Mettre a jour" : "Ajouter"}
                                  </button>
                                  {categoryForm.id ? (
                                    <button className="btn btn-outline-dark" type="button" onClick={resetCategoryForm}>
                                      Annuler
                                    </button>
                                  ) : null}
                                </div>
                              </div>
                            </form>
                          </div>
                        </div>
                      </div>
                      <div className="col-lg-7">
                        <div className="card border-0 shadow-sm h-100">
                          <div className="card-body p-4">
                            <h5 className="mb-4">Liste des categories</h5>
                            <div className="table-responsive">
                              <table className="table mb-0">
                                <thead className="table-light">
                                  <tr>
                                    <th>Nom</th>
                                    <th>Ordre</th>
                                    <th>Etat</th>
                                    <th>Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {categories.map((category) => (
                                    <tr key={category.id}>
                                      <td>
                                        <div className="fw-semibold">{category.name}</div>
                                        {category.description ? <small className="text-muted">{category.description}</small> : null}
                                      </td>
                                      <td>{category.sort_order || 0}</td>
                                      <td>
                                        <span className={`badge ${category.is_active ? "bg-success" : "bg-secondary"}`}>
                                          {category.is_active ? "Active" : "Inactive"}
                                        </span>
                                      </td>
                                      <td>
                                        <div className="d-flex gap-2 flex-wrap">
                                          <button className="btn btn-outline-dark btn-sm" type="button" onClick={() => handleCategoryEdit(category)}>
                                            Modifier
                                          </button>
                                          <button className="btn btn-outline-danger btn-sm" type="button" onClick={() => handleCategoryDelete(category.id)}>
                                            Supprimer
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                  )}

                  {activeTab === "variantes" && (
                  <div className="row g-4 mb-8">
                    <div className="col-lg-5">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body p-4">
                          <div className="d-flex justify-content-between align-items-center mb-4">
                            <h5 className="mb-0">Variants</h5>
                            <button className="btn btn-outline-dark btn-sm" type="button" onClick={resetVariantForm}>
                              Nouveau
                            </button>
                          </div>
                          <div className="mb-3">
                            <label className="form-label">Produit</label>
                            <select
                              className="form-select"
                              value={selectedVariantProductId}
                              onChange={(event) => setSelectedVariantProductId(event.target.value)}
                            >
                              <option value="">Choisir un produit</option>
                              {products.map((product) => (
                                <option key={product.id} value={product.id}>
                                  {product.name}
                                </option>
                              ))}
                            </select>
                          </div>
                          <form onSubmit={handleVariantSubmit}>
                            <div className="row g-3">
                              <div className="col-12">
                                <label className="form-label">Nom</label>
                                <input name="name" className="form-control" value={variantForm.name} onChange={handleVariantChange} required />
                              </div>
                              <div className="col-12">
                                <label className="form-label">SKU</label>
                                <input name="sku" className="form-control" value={variantForm.sku} onChange={handleVariantChange} required />
                              </div>
                              <div className="col-md-6">
                                <label className="form-label">Taille</label>
                                <input name="size_label" className="form-control" value={variantForm.size_label} onChange={handleVariantChange} placeholder="500g, 1kg..." />
                              </div>
                              <div className="col-md-3">
                                <label className="form-label">Poids</label>
                                <input name="weight_value" type="number" step="0.001" className="form-control" value={variantForm.weight_value} onChange={handleVariantChange} />
                              </div>
                              <div className="col-md-3">
                                <label className="form-label">Unite</label>
                                <select name="weight_unit" className="form-select" value={variantForm.weight_unit} onChange={handleVariantChange}>
                                  <option value="g">g</option>
                                  <option value="kg">kg</option>
                                </select>
                              </div>
                              <div className="col-md-6">
                                <label className="form-label">Prix override</label>
                                <input name="price_override" type="number" step="0.01" className="form-control" value={variantForm.price_override} onChange={handleVariantChange} />
                              </div>
                              <div className="col-12">
                                <div className="form-check">
                                  <input id="defaultVariant" name="is_default" type="checkbox" className="form-check-input" checked={variantForm.is_default} onChange={handleVariantChange} />
                                  <label className="form-check-label" htmlFor="defaultVariant">Variant par defaut</label>
                                </div>
                              </div>
                              {variantMessage ? (
                                <div className="col-12">
                                  <div className="alert alert-success mb-0">{variantMessage}</div>
                                </div>
                              ) : null}
                              <div className="col-12 d-flex gap-2">
                                <button className="btn btn-primary" type="submit" disabled={!selectedVariantProductId}>
                                  {variantForm.id ? "Mettre a jour" : "Ajouter"}
                                </button>
                                {variantForm.id ? (
                                  <button className="btn btn-outline-dark" type="button" onClick={resetVariantForm}>
                                    Annuler
                                  </button>
                                ) : null}
                              </div>
                            </div>
                          </form>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-7">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body p-4">
                          <h5 className="mb-4">Variants du produit</h5>
                          <div className="table-responsive">
                            <table className="table mb-0">
                              <thead className="table-light">
                                <tr>
                                  <th>Nom</th>
                                  <th>SKU</th>
                                  <th>Poids</th>
                                  <th>Prix</th>
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {variants.map((variant) => (
                                  <tr key={variant.id}>
                                    <td>
                                      <div className="fw-semibold">{variant.name}</div>
                                      {variant.is_default ? <small className="text-success">Variant par defaut</small> : null}
                                    </td>
                                    <td>{variant.sku}</td>
                                    <td>{variant.weight_value ? `${variant.weight_value} ${variant.weight_unit}` : variant.size_label || "-"}</td>
                                    <td>{variant.price_override ? formatAdminPrice(variant.price_override) : "-"}</td>
                                    <td>
                                      <div className="d-flex gap-2 flex-wrap">
                                        <button className="btn btn-outline-dark btn-sm" type="button" onClick={() => handleVariantEdit(variant)}>
                                          Modifier
                                        </button>
                                        <button className="btn btn-outline-danger btn-sm" type="button" onClick={() => handleVariantDelete(variant.id)}>
                                          Supprimer
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                                {!variants.length ? (
                                  <tr>
                                    <td colSpan="5" className="text-muted">
                                      Aucun variant pour ce produit.
                                    </td>
                                  </tr>
                                ) : null}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  )}

                  {activeTab === "menues" && (
                    <div className="row g-4">
                      <div className="col-lg-5">
                        <div className="card border-0 shadow-sm h-100">
                          <div className="card-body p-4">
                            <h5 className="mb-4">
                              {editingMenuId ? "Modifier le menu" : "Nouveau menu"}
                              <span className="badge ms-2 text-bg-warning">Traiteur</span>
                            </h5>
                            <form onSubmit={handleMenuSubmit}>
                              <div className="mb-3">
                                <label className="form-label">Nom du menu *</label>
                                <input name="name" className="form-control" value={menuForm.name} onChange={handleMenuChange} placeholder="Ex: Menu 1, Zaatar..." required />
                              </div>
                              <div className="mb-3">
                                <label className="form-label">Prix (TTC) *</label>
                                <div className="input-group">
                                  <input type="number" step="0.01" min="0" name="price" className="form-control" value={menuForm.price} onChange={handleMenuChange} placeholder="0.00" required />
                                  <span className="input-group-text">€</span>
                                </div>
                              </div>
                              <div className="mb-3">
                                <label className="form-label">Composants <small className="text-muted">(un par ligne)</small></label>
                                <textarea name="componentsText" className="form-control" rows={5} value={menuForm.componentsText} onChange={handleMenuChange} placeholder={"1 Sandwich Zaatar\n1 Boisson\n1 Dessert"} />
                              </div>
                              <div className="mb-4">
                                <label className="form-label">Position <small className="text-muted">(ordre dans la caisse)</small></label>
                                <input type="number" name="sortOrder" className="form-control" value={menuForm.sortOrder} onChange={handleMenuChange} />
                              </div>
                              <div className="d-flex gap-2">
                                <button type="submit" className="btn btn-warning">
                                  {editingMenuId ? "Enregistrer" : "Ajouter le menu"}
                                </button>
                                {editingMenuId ? (
                                  <button type="button" className="btn btn-outline-dark" onClick={resetMenuForm}>
                                    Annuler
                                  </button>
                                ) : null}
                              </div>
                            </form>
                            {menuMessage ? (
                              <div className="alert alert-info mt-3 mb-0">
                                {menuMessage}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </div>
                      <div className="col-lg-7">
                        <div className="card border-0 shadow-sm h-100">
                          <div className="card-body p-4">
                            <h5 className="mb-4">Menues de la caisse Traiteur</h5>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <span className="text-muted">{menus.length} menu(s)</span>
                              <button className="btn btn-outline-primary text-bg-warning" type="button" onClick={resetMenuForm} style={{ backgroundColor: "#f97316", borderColor: "#f97316", color: "#fff" }}>
                                <i className="bi bi-plus-lg me-1"></i>Nouveau menu
                              </button>
                            </div>
                            <div className="table-responsive">
                              <table className="table mb-0">
                                <thead className="table-light">
                                  <tr>
                                    <th>Menu</th>
                                    <th>Prix</th>
                                    <th>Composants</th>
                                    <th>Pos.</th>
                                    <th>Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {menus.map((menu) => (
                                    <tr key={menu.id}>
                                      <td>
                                        <div className="fw-semibold">{menu.name}</div>
                                        <small className="text-muted">Traiteur · caisse</small>
                                      </td>
                                      <td>{formatAdminPrice(menu.price)}</td>
                                      <td>
                                        <small>{menu.components.length > 0 ? menu.components.join(" · ") : "-"}</small>
                                      </td>
                                      <td>{menu.sortOrder ?? 0}</td>
                                      <td>
                                        <div className="d-flex gap-2 flex-wrap">
                                          <button className="btn btn-outline-dark btn-sm" type="button" onClick={() => handleMenuEdit(menu)}>
                                            Modifier
                                          </button>
                                          <button className="btn btn-outline-danger btn-sm" type="button" onClick={() => handleMenuDelete(menu.id)}>
                                            Supprimer
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                  {!menus.length ? (
                                    <tr>
                                      <td colSpan="5" className="text-muted">
                                        Aucun menu. Créez votre premier menu à gauche.
                                      </td>
                                    </tr>
                                  ) : null}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === "coupons" && (
                    <div className="card border-0 shadow-sm">
                      <div className="card-body p-4">
                        <h5 className="mb-4">Gestion des coupons promo</h5>
                        {couponMessage && <div className="alert alert-info py-2">{couponMessage}</div>}
                        <div className="row g-4">
                          <div className="col-md-5">
                            <div className="border rounded-4 p-3 bg-white">
                              <h6 className="mb-3">{editingCouponId ? "Modifier le coupon" : "Creer un coupon"}</h6>
                              <div className="mb-2">
                                <label className="form-label small">Code promo</label>
                                <input type="text" className="form-control text-uppercase" value={couponForm.code} onChange={(e) => setCouponForm({ ...couponForm, code: e.target.value })} placeholder="Ex: ETE2026" />
                              </div>
                              <div className="row g-2 mb-2">
                                <div className="col-6">
                                  <label className="form-label small">Type</label>
                                  <select className="form-select" value={couponForm.type} onChange={(e) => setCouponForm({ ...couponForm, type: e.target.value })}>
                                    {couponTypeOptions.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                  </select>
                                </div>
                                <div className="col-6">
                                  <label className="form-label small">{couponForm.type === "percent" ? "Valeur (%)" : "Montant (EUR)"}</label>
                                  <input type="number" min="0" step="0.01" className="form-control" value={couponForm.value} onChange={(e) => setCouponForm({ ...couponForm, value: e.target.value })} />
                                </div>
                              </div>
                              <div className="row g-2 mb-2">
                                <div className="col-6">
                                  <label className="form-label small">Commande min (EUR)</label>
                                  <input type="number" min="0" step="0.01" className="form-control" value={couponForm.minOrder} onChange={(e) => setCouponForm({ ...couponForm, minOrder: e.target.value })} placeholder="0 = pas min" />
                                </div>
                                <div className="col-6">
                                  <label className="form-label small">Utilisations max</label>
                                  <input type="number" min="0" className="form-control" value={couponForm.maxUses} onChange={(e) => setCouponForm({ ...couponForm, maxUses: e.target.value })} placeholder="0 = illimite" />
                                </div>
                              </div>
                              <div className="mb-2">
                                <label className="form-label small">Description</label>
                                <input type="text" className="form-control" value={couponForm.description} onChange={(e) => setCouponForm({ ...couponForm, description: e.target.value })} placeholder="Description du coupon" />
                              </div>
                              <div className="form-check mb-3">
                                <input type="checkbox" className="form-check-input" checked={couponForm.active} onChange={(e) => setCouponForm({ ...couponForm, active: e.target.checked })} />
                                <label className="form-check-label small">Actif</label>
                              </div>
                              <div className="d-flex gap-2">
                                <button type="button" className="btn btn-dark flex-grow-1" onClick={async () => {
                                  if (!couponForm.code.trim()) { setCouponMessage("Le code promo est obligatoire."); return; }
                                  if (!couponForm.value || Number(couponForm.value) <= 0) { setCouponMessage("La valeur du remise est obligatoire."); return; }
                                  const updated = await saveCoupon({
                                    id: editingCouponId || undefined,
                                    code: couponForm.code.trim().toUpperCase(),
                                    type: couponForm.type,
                                    value: Number(couponForm.value),
                                    minOrder: Number(couponForm.minOrder || 0),
                                    maxUses: Number(couponForm.maxUses || 0),
                                    description: couponForm.description,
                                    active: couponForm.active,
                                  });
                                  setCoupons(updated);
                                  setCouponForm({ code: "", type: "percent", value: "", minOrder: "", maxUses: "", description: "", active: true });
                                  setEditingCouponId(null);
                                  setCouponMessage(editingCouponId ? "Coupon modifie avec succes." : "Coupon cree avec succes.");
                                }}>{editingCouponId ? "Modifier" : "Creer"}</button>
                                {editingCouponId && <button type="button" className="btn btn-outline-secondary" onClick={() => { setEditingCouponId(null); setCouponForm({ code: "", type: "percent", value: "", minOrder: "", maxUses: "", description: "", active: true }); }}>Annuler</button>}
                              </div>
                            </div>
                          </div>
                          <div className="col-md-7">
                            <div className="table-responsive">
                              <table className="table mb-0">
                                <thead className="table-light">
                                  <tr>
                                    <th>Code</th>
                                    <th>Type</th>
                                    <th>Valeur</th>
                                    <th>Min cmd</th>
                                    <th>Utilisations</th>
                                    <th>Statut</th>
                                    <th>Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {coupons.map((c) => (
                                    <tr key={c.id}>
                                      <td><strong className="text-uppercase">{c.code}</strong><br /><small className="text-muted">{c.description}</small></td>
                                      <td>{couponTypeOptions.find((o) => o.value === c.type)?.label || c.type}</td>
                                      <td>{c.type === "percent" ? `${c.value}%` : `${c.value.toFixed(2)} EUR`}</td>
                                      <td>{c.minOrder > 0 ? `${c.minOrder.toFixed(2)} EUR` : "—"}</td>
                                      <td>{c.useCount}{c.maxUses > 0 ? ` / ${c.maxUses}` : " / ∞"}</td>
                                      <td><span className={`badge ${c.active ? "bg-success" : "bg-secondary"}`}>{c.active ? "Actif" : "Inactif"}</span></td>
                                      <td>
                                        <div className="d-flex gap-1">
                                          <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => { setEditingCouponId(c.id); setCouponForm({ code: c.code, type: c.type, value: String(c.value), minOrder: String(c.minOrder || ""), maxUses: String(c.maxUses || ""), description: c.description, active: c.active }); }}>Modifier</button>
                                          <button type="button" className="btn btn-outline-danger btn-sm" onClick={async () => { if (!window.confirm(`Supprimer le coupon ${c.code} ?`)) return; await deleteCoupon(c.id); setCoupons((prev) => prev.filter((x) => x.id !== c.id)); setCouponMessage("Coupon supprime."); }}>Supprimer</button>
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                  {!coupons.length && <tr><td colSpan="7" className="text-center text-muted py-4">Aucun coupon cree.</td></tr>}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === "giftcards" && (
                    <div className="card border-0 shadow-sm">
                      <div className="card-body p-4">
                        <h5 className="mb-4">Gestion des cartes cadeaux</h5>
                        {giftCardMessage && <div className="alert alert-info py-2">{giftCardMessage}</div>}

                        <div className="row g-3 mb-4">
                          <div className="col-md-3">
                            <div className="card border-0 shadow-sm h-100">
                              <div className="card-body p-3">
                                <p className="text-muted mb-1 small">Total cartes</p>
                                <h4 className="mb-0">{giftCards.length}</h4>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="card border-0 shadow-sm h-100">
                              <div className="card-body p-3">
                                <p className="text-muted mb-1 small">Actives</p>
                                <h4 className="mb-0 text-success">{giftCards.filter((g) => g.status === "active").length}</h4>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="card border-0 shadow-sm h-100">
                              <div className="card-body p-3">
                                <p className="text-muted mb-1 small">Solde total</p>
                                <h4 className="mb-0">{giftCards.reduce((s, g) => s + g.currentBalance, 0).toFixed(2)} EUR</h4>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="card border-0 shadow-sm h-100">
                              <div className="card-body p-3">
                                <p className="text-muted mb-1 small">Utilisees / Expirees</p>
                                <h4 className="mb-0">{giftCards.filter((g) => g.status === "used" || g.status === "expired").length}</h4>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="row g-4 mb-4">
                          <div className="col-md-5">
                            <div className="border rounded-4 p-3 bg-white">
                              <h6 className="mb-3">{editingGiftCardId ? "Modifier la carte" : "Creer des cartes cadeaux"}</h6>
                              <div className="mb-2">
                                <label className="form-label small">Solde (EUR)</label>
                                <input type="number" min="0" step="0.01" className="form-control" value={editingGiftCardId ? giftCardEditForm.balance || "" : giftCardForm.balance} onChange={(e) => editingGiftCardId ? setGiftCardEditForm({ ...giftCardEditForm, balance: e.target.value }) : setGiftCardForm({ ...giftCardForm, balance: e.target.value })} />
                              </div>
                              {!editingGiftCardId && (
                                <div className="mb-2">
                                  <label className="form-label small">Quantite</label>
                                  <input type="number" min="1" max="50" className="form-control" value={giftCardForm.count} onChange={(e) => setGiftCardForm({ ...giftCardForm, count: e.target.value })} />
                                </div>
                              )}
                              <div className="row g-2 mb-2">
                                <div className="col-6">
                                  <label className="form-label small">Expiration</label>
                                  <input type="date" className="form-control" value={editingGiftCardId ? giftCardEditForm.expiryDate || "" : giftCardForm.expiryDate} onChange={(e) => editingGiftCardId ? setGiftCardEditForm({ ...giftCardEditForm, expiryDate: e.target.value }) : setGiftCardForm({ ...giftCardForm, expiryDate: e.target.value })} />
                                </div>
                                <div className="col-6">
                                  <label className="form-label small">Cmd min (EUR)</label>
                                  <input type="number" min="0" step="0.01" className="form-control" value={editingGiftCardId ? giftCardEditForm.minOrder || "" : giftCardForm.minOrder} onChange={(e) => editingGiftCardId ? setGiftCardEditForm({ ...giftCardEditForm, minOrder: e.target.value }) : setGiftCardForm({ ...giftCardForm, minOrder: e.target.value })} />
                                </div>
                              </div>
                              <div className="mb-2">
                                <label className="form-label small">Nom du beneficiaire</label>
                                <input type="text" className="form-control" value={editingGiftCardId ? giftCardEditForm.recipientName || "" : giftCardForm.recipientName} onChange={(e) => editingGiftCardId ? setGiftCardEditForm({ ...giftCardEditForm, recipientName: e.target.value }) : setGiftCardForm({ ...giftCardForm, recipientName: e.target.value })} placeholder="Optionnel" />
                              </div>
                              <div className="mb-3">
                                <label className="form-label small">Note</label>
                                <input type="text" className="form-control" value={editingGiftCardId ? giftCardEditForm.note || "" : giftCardForm.note} onChange={(e) => editingGiftCardId ? setGiftCardEditForm({ ...giftCardEditForm, note: e.target.value }) : setGiftCardForm({ ...giftCardForm, note: e.target.value })} placeholder="Optionnel" />
                              </div>
                              {editingGiftCardId && (
                                <div className="mb-3">
                                  <label className="form-label small">Statut</label>
                                  <select className="form-select" value={giftCardEditForm.status || "active"} onChange={(e) => setGiftCardEditForm({ ...giftCardEditForm, status: e.target.value })}>
                                    {giftCardStatusOptions.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                  </select>
                                </div>
                              )}
                              <div className="d-flex gap-2">
                                <button type="button" className="btn btn-dark flex-grow-1" onClick={async () => {
                                  if (editingGiftCardId) {
                                    const updated = await saveGiftCard({ ...giftCards.find((g) => g.id === editingGiftCardId), ...giftCardEditForm, balance: Number(giftCardEditForm.balance), minOrder: Number(giftCardEditForm.minOrder || 0), currentBalance: Number(giftCardEditForm.balance) });
                                    setGiftCards(updated);
                                    setEditingGiftCardId(null);
                                    setGiftCardEditForm({});
                                    setGiftCardMessage("Carte modifiee.");
                                  } else {
                                    if (!giftCardForm.balance || Number(giftCardForm.balance) <= 0) { setGiftCardMessage("Le solde est obligatoire."); return; }
                                    const count = Math.min(Number(giftCardForm.count || 1), 50);
                                    const created = await createGiftCardBatch({ count, balance: Number(giftCardForm.balance), expiryDate: giftCardForm.expiryDate || null, minOrder: Number(giftCardForm.minOrder || 0), note: giftCardForm.note, recipientName: giftCardForm.recipientName });
                                    const refreshed = await loadGiftCards();
                                    setGiftCards(refreshed);
                                    setCreatedGiftCardCodes(created.map((c) => c.code));
                                    setGiftCardForm({ balance: "", expiryDate: "", minOrder: "", count: 1, note: "", recipientName: "" });
                                    setGiftCardMessage(`${created.length} carte(s) creee(s) avec succes.`);
                                  }
                                }}>{editingGiftCardId ? "Modifier" : "Creer"}</button>
                                {editingGiftCardId && <button type="button" className="btn btn-outline-secondary" onClick={() => { setEditingGiftCardId(null); setGiftCardEditForm({}); }}>Annuler</button>}
                              </div>
                            </div>
                          </div>
                          <div className="col-md-7">
                            {createdGiftCardCodes.length > 0 && (
                              <div className="border rounded-4 p-3 mb-3 bg-light">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <h6 className="mb-0">Cartes creees</h6>
                                  <button type="button" className="btn-close" onClick={() => setCreatedGiftCardCodes([])} />
                                </div>
                                <div style={{ maxHeight: "120px", overflowY: "auto" }}>
                                  {createdGiftCardCodes.map((code, idx) => (
                                    <code key={idx} className="d-block mb-1 fw-bold">{code}</code>
                                  ))}
                                </div>
                              </div>
                            )}
                            <div className="d-flex gap-2 mb-3 flex-wrap">
                              <input type="search" className="form-control" style={{ minWidth: "200px" }} placeholder="Rechercher par code, nom..." value={giftCardSearch} onChange={(e) => setGiftCardSearch(e.target.value)} />
                              <select className="form-select" style={{ width: "auto" }} value={giftCardStatusFilter} onChange={(e) => setGiftCardStatusFilter(e.target.value)}>
                                <option value="all">Tous les statuts</option>
                                {giftCardStatusOptions.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                              </select>
                              <small className="text-muted align-self-center">{giftCards.filter((g) => {
                                const matchSearch = !giftCardSearch || g.code.toLowerCase().includes(giftCardSearch.toLowerCase()) || (g.recipientName && g.recipientName.toLowerCase().includes(giftCardSearch.toLowerCase()));
                                const matchStatus = giftCardStatusFilter === "all" || g.status === giftCardStatusFilter;
                                return matchSearch && matchStatus;
                              }).length} resultat(s)</small>
                            </div>
                            <div className="table-responsive">
                              <table className="table mb-0">
                                <thead className="table-light">
                                  <tr>
                                    <th>Code</th>
                                    <th>Solde</th>
                                    <th>Expiration</th>
                                    <th>Statut</th>
                                    <th>Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {giftCards.filter((g) => {
                                    const matchSearch = !giftCardSearch || g.code.toLowerCase().includes(giftCardSearch.toLowerCase()) || (g.recipientName && g.recipientName.toLowerCase().includes(giftCardSearch.toLowerCase()));
                                    const matchStatus = giftCardStatusFilter === "all" || g.status === giftCardStatusFilter;
                                    return matchSearch && matchStatus;
                                  }).map((g) => (
                                    <React.Fragment key={g.id}>
                                      <tr>
                                        <td>
                                          <strong style={{ fontFamily: "monospace", letterSpacing: "0.05em" }}>{g.code}</strong>
                                          {g.recipientName && <><br /><small className="text-muted">{g.recipientName}</small></>}
                                        </td>
                                        <td>{g.currentBalance.toFixed(2)} EUR<br /><small className="text-muted">/{g.initialBalance.toFixed(2)} EUR</small></td>
                                        <td>{g.expiryDate || "—"}</td>
                                        <td><span className={`badge ${giftCardStatusBadgeClass(g.status)}`}>{giftCardStatusBadgeLabel(g.status)}</span></td>
                                        <td>
                                          <div className="d-flex gap-1 flex-wrap">
                                            <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => { setExpandedGiftCardId(expandedGiftCardId === g.id ? null : g.id); if (expandedGiftCardId !== g.id) { setGiftCardTransactions(getGiftCardTransactions(g.id)); } }}>Historique</button>
                                            <button type="button" className="btn btn-outline-dark btn-sm" onClick={() => { setEditingGiftCardId(g.id); setGiftCardEditForm({ balance: String(g.currentBalance), expiryDate: g.expiryDate || "", minOrder: String(g.minOrder || ""), recipientName: g.recipientName || "", note: g.note || "", status: g.status }); }}>Modifier</button>
                                            <button type="button" className="btn btn-outline-danger btn-sm" onClick={async () => { if (!window.confirm(`Supprimer la carte ${g.code} ?`)) return; await deleteGiftCard(g.id); setGiftCards((prev) => prev.filter((x) => x.id !== g.id)); setGiftCardMessage("Carte supprimee."); }}>Supprimer</button>
                                          </div>
                                        </td>
                                      </tr>
                                      {expandedGiftCardId === g.id && (
                                        <tr>
                                          <td colSpan="5" className="bg-light">
                                            <div className="py-2">
                                              <strong className="small">Transactions :</strong>
                                              {giftCardTransactions.length > 0 ? (
                                                <div className="table-responsive">
                                                  <table className="table table-sm mb-0 mt-1">
                                                    <thead>
                                                      <tr>
                                                        <th>Date</th>
                                                        <th>Commande</th>
                                                        <th>Montant retire</th>
                                                        <th>Solde avant</th>
                                                        <th>Solde apres</th>
                                                      </tr>
                                                    </thead>
                                                    <tbody>
                                                      {giftCardTransactions.map((tx) => (
                                                        <tr key={tx.id}>
                                                          <td><small>{new Date(tx.createdAt).toLocaleDateString("fr-FR")}</small></td>
                                                          <td><small>{tx.orderId || "—"}</small></td>
                                                          <td><small className={tx.amountDeducted > 0 ? "text-danger" : "text-success"}>{tx.amountDeducted > 0 ? "-" : "+"}{tx.amountDeducted.toFixed(2)} EUR</small></td>
                                                          <td><small>{tx.balanceBefore.toFixed(2)} EUR</small></td>
                                                          <td><small>{tx.balanceAfter.toFixed(2)} EUR</small></td>
                                                        </tr>
                                                      ))}
                                                    </tbody>
                                                  </table>
                                                </div>
                                              ) : (
                                                <p className="text-muted small mb-0 mt-1">Aucune transaction.</p>
                                              )}
                                            </div>
                                          </td>
                                        </tr>
                                      )}
                                    </React.Fragment>
                                  ))}
                                  {!giftCards.length && (
                                    <tr>
                                      <td colSpan="5" className="text-center text-muted py-4">Aucune carte cadeau creee.</td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === "loyalty" && (
                    <div className="card border-0 shadow-sm">
                      <div className="card-body p-4">
                        <div className="row g-3 mb-4">
                          <div className="col-md-3">
                            <div className="card border-0 shadow-sm text-center p-3">
                              <small className="text-muted">Clients fideles</small>
                              <h4 className="mb-0 mt-1">{loyaltyPointsList.length}</h4>
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="card border-0 shadow-sm text-center p-3">
                              <small className="text-muted">Total points</small>
                              <h4 className="mb-0 mt-1 text-primary">{loyaltyPointsList.reduce((s, r) => s + r.balance, 0)}</h4>
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="card border-0 shadow-sm text-center p-3">
                              <small className="text-muted">Gagnes ce mois</small>
                              <h4 className="mb-0 mt-1 text-success">{earnedThisMonth}</h4>
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="card border-0 shadow-sm text-center p-3">
                              <small className="text-muted">Utilises ce mois</small>
                              <h4 className="mb-0 mt-1 text-warning">{redeemedThisMonth}</h4>
                            </div>
                          </div>
                        </div>

                        <div className="mb-3">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Rechercher par nom ou telephone..."
                            value={loyaltySearch}
                            onChange={(e) => setLoyaltySearch(e.target.value)}
                          />
                        </div>

                        {loyaltyMessage && (
                          <div className="alert alert-info py-2">{loyaltyMessage}</div>
                        )}

                        <div className="table-responsive">
                          <table className="table table-hover align-middle">
                            <thead className="table-light">
                              <tr>
                                <th>Client</th>
                                <th>Telephone</th>
                                <th>Points</th>
                                <th>Derniere activite</th>
                                <th></th>
                              </tr>
                            </thead>
                            <tbody>
                              {loyaltyPointsList.filter((r) => {
                                if (!loyaltySearch) return true;
                                const q = loyaltySearch.toLowerCase();
                                return (r.customerName || "").toLowerCase().includes(q) || (r.customerPhone || "").includes(q);
                              }).length === 0 && (
                                <tr>
                                  <td colSpan="5" className="text-center text-muted py-4">
                                    Aucun client fidele trouve.
                                  </td>
                                </tr>
                              )}
                              {loyaltyPointsList.filter((r) => {
                                if (!loyaltySearch) return true;
                                const q = loyaltySearch.toLowerCase();
                                return (r.customerName || "").toLowerCase().includes(q) || (r.customerPhone || "").includes(q);
                              }).map((r) => (
                                <tr
                                  key={r.id}
                                  className={selectedLoyaltyCustomer?.id === r.id ? "table-active" : ""}
                                  style={{ cursor: "pointer" }}
                                  onClick={async () => {
                                    setSelectedLoyaltyCustomer(r);
                                    const txns = await getLoyaltyTransactions(r.customerId);
                                    setLoyaltyTransactions(txns);
                                  }}
                                >
                                  <td className="fw-semibold">{r.customerName || "Client"}</td>
                                  <td>{r.customerPhone || "-"}</td>
                                  <td><span className="badge bg-primary rounded-pill">{r.balance} pts</span></td>
                                  <td><small className="text-muted">{r.updatedAt ? new Date(r.updatedAt).toLocaleDateString("fr-FR") : "-"}</small></td>
                                  <td>
                                    <button
                                      className="btn btn-sm btn-outline-primary"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedLoyaltyCustomer(r);
                                        getLoyaltyTransactions(r.customerId).then(setLoyaltyTransactions);
                                      }}
                                    >
                                      Detail
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {selectedLoyaltyCustomer && (
                          <div className="card border-0 shadow-sm mt-3">
                            <div className="card-body p-4">
                              <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="mb-0">{selectedLoyaltyCustomer.customerName || "Client"}</h5>
                                <button className="btn btn-sm btn-outline-secondary" onClick={() => { setSelectedLoyaltyCustomer(null); setLoyaltyTransactions([]); }}>
                                  Fermer
                                </button>
                              </div>
                              <div className="row g-3 mb-3">
                                <div className="col-md-4">
                                  <small className="text-muted">Telephone</small>
                                  <div>{selectedLoyaltyCustomer.customerPhone || "-"}</div>
                                </div>
                                <div className="col-md-4">
                                  <small className="text-muted">Solde</small>
                                  <div><span className="badge bg-primary fs-6">{selectedLoyaltyCustomer.balance} points</span></div>
                                </div>
                                <div className="col-md-4">
                                  <small className="text-muted">Total gagne / utilise</small>
                                  <div>{selectedLoyaltyCustomer.totalEarned} / {selectedLoyaltyCustomer.totalRedeemed}</div>
                                </div>
                              </div>

                              <h6 className="mt-4 mb-3">Historique des transactions</h6>
                              {loyaltyTransactions.length === 0 ? (
                                <p className="text-muted">Aucune transaction.</p>
                              ) : (
                                <div className="table-responsive">
                                  <table className="table table-sm align-middle">
                                    <thead className="table-light">
                                      <tr>
                                        <th>Date</th>
                                        <th>Type</th>
                                        <th>Points</th>
                                        <th>Commande</th>
                                        <th>Note</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {loyaltyTransactions.map((t) => (
                                        <tr key={t.id}>
                                          <td><small>{new Date(t.createdAt).toLocaleDateString("fr-FR")}</small></td>
                                          <td>
                                            <span className={`badge ${t.type === "earn" ? "bg-success" : "bg-warning text-dark"}`}>
                                              {t.type === "earn" ? "Gain" : "Utilisation"}
                                            </span>
                                          </td>
                                          <td className={t.type === "earn" ? "text-success" : "text-warning"}>
                                            {t.type === "earn" ? "+" : "-"}{t.points}
                                          </td>
                                          <td><small className="text-muted">{t.orderId || "-"}</small></td>
                                          <td><small>{t.note || "-"}</small></td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}

                              <div className="mt-3 p-3 bg-light rounded">
                                <h6>Ajustement manuel</h6>
                                <div className="row g-2 align-items-end">
                                  <div className="col-md-3">
                                    <label className="form-label small">Points (+ ou -)</label>
                                    <input
                                      type="number"
                                      className="form-control form-control-sm"
                                      value={loyaltyAdjustAmount}
                                      onChange={(e) => setLoyaltyAdjustAmount(e.target.value)}
                                      placeholder="ex: 10 ou -5"
                                    />
                                  </div>
                                  <div className="col-md-5">
                                    <label className="form-label small">Note</label>
                                    <input
                                      type="text"
                                      className="form-control form-control-sm"
                                      value={loyaltyAdjustNote}
                                      onChange={(e) => setLoyaltyAdjustNote(e.target.value)}
                                      placeholder="Motif de l'ajustement"
                                    />
                                  </div>
                                  <div className="col-md-4">
                                    <button
                                      className="btn btn-sm btn-primary"
                                      onClick={async () => {
                                        const amt = Number(loyaltyAdjustAmount);
                                        if (!amt || !selectedLoyaltyCustomer) return;
                                        const updated = await adjustPoints(selectedLoyaltyCustomer.customerId, amt, loyaltyAdjustNote);
                                        if (updated) {
                                          setSelectedLoyaltyCustomer(updated);
                                          setLoyaltyPointsList((prev) => prev.map((r) => r.id === updated.id ? updated : r));
                                          const txns = await getLoyaltyTransactions(updated.customerId);
                                          setLoyaltyTransactions(txns);
                                          setLoyaltyAdjustAmount("");
                                          setLoyaltyAdjustNote("");
                                          setLoyaltyMessage(`Ajustement ${amt > 0 ? "+" : ""}${amt} points effectue.`);
                                          setTimeout(() => setLoyaltyMessage(""), 3000);
                                        }
                                      }}
                                    >
                                      Appliquer
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {activeTab === "produits" && (
                  <div className="card border-0 shadow-sm">
                    <div className="card-body p-4">
                      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                        <h5 className="mb-0">Produits enregistres</h5>
                        <div className="d-flex align-items-center gap-3 flex-wrap">
                          <input
                            type="search"
                            className="form-control"
                            style={{ minWidth: "260px" }}
                            placeholder="Rechercher un produit, SKU, categorie..."
                            value={productSearch}
                            onChange={(event) => setProductSearch(event.target.value)}
                          />
                          <select
                            className="form-select"
                            style={{ width: "auto", minWidth: "160px" }}
                            value={unitFilter}
                            onChange={(event) => setUnitFilter(event.target.value)}
                          >
                            <option value="all">Toutes unites</option>
                            <option value="alimentation">Alimentation</option>
                            <option value="traiteur">Traiteur</option>
                          </select>
                          <small className="text-muted">
                            {filteredProducts.length} resultat(s)
                          </small>
                        </div>
                      </div>
                        <div className="table-responsive">
                          <table className="table table-sm align-middle mb-0" style={{ fontSize: "0.75rem", tableLayout: "fixed", width: "100%" }}>
                            <thead className="table-light">
                              <tr>
                                <th className="fw-semibold" style={{ width: "18%" }}>Produit</th>
                                <th className="fw-semibold" style={{ width: "11%" }}>Categorie</th>
                                <th className="fw-semibold" style={{ width: "9%" }}>Type</th>
                                <th className="fw-semibold" style={{ width: "9%" }}>Vente</th>
                                <th className="fw-semibold" style={{ width: "8%" }}>Prix</th>
                                <th className="fw-semibold" style={{ width: "6%" }}>Stock</th>
                                <th className="fw-semibold" style={{ width: "11%" }}>Restriction</th>
                                <th className="fw-semibold" style={{ width: "12%" }}>Code-barres</th>
                                <th className="fw-semibold" style={{ width: "7%" }}>Fournisseur</th>
                                <th className="fw-semibold" style={{ width: "9%" }}>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {paginatedProducts.map((product) => {
                                const supplierName = product.supplierId
                                  ? suppliers.find((s) => s.id === product.supplierId)?.name || product.supplierId
                                  : "";
                                const restrictionText = [
                                  product.maxPerOrder ? `Max ${product.maxPerOrder}` : "",
                                  product.allowedZoneIds?.length ? "Zones limitees" : "Toutes zones",
                                ].filter(Boolean).join(" - ");
                                const weightLine = product.saleType !== "unit" && product.minWeightKg
                                  ? `Min ${product.minWeightKg}kg / pas ${product.stepWeightKg || 0.05}kg`
                                  : "";
                                const saleText = [product.saleType || "unit", product.variantLabel, weightLine].filter(Boolean).join(" / ");
                                return (
                                  <tr key={product.id} onClick={() => handleEdit(product)} style={{ cursor: "pointer" }} className="table-row-hover">
                                    <td title={product.name}>
                                      <div className="ck-truncate fw-semibold">{product.name}</div>
                                      {product.featured ? <small className="text-success ck-nowrap">En avant</small> : null}
                                    </td>
                                    <td title={product.category}>
                                      <span className="ck-truncate d-block">{product.category}</span>
                                    </td>
                                    <td title={product.type}>
                                      <span className="badge bg-light text-dark border text-uppercase ck-truncate d-block">{product.type}</span>
                                      {product.antiGaspiDiscount ? (
                                        <span className="badge bg-success mt-1 ck-nowrap">🌿 -{product.antiGaspiDiscount}%</span>
                                      ) : null}
                                    </td>
                                    <td title={saleText}>
                                      <span className="ck-truncate d-block text-uppercase">{product.saleType || "unit"}</span>
                                      {product.variantLabel ? <small className="text-muted ck-truncate d-block">{product.variantLabel}</small> : null}
                                    </td>
                                    <td className="ck-nowrap">{formatAdminPrice(product.price)}</td>
                                    <td>
                                      <span className={`ck-nowrap ${product.stock <= LOW_STOCK_THRESHOLD ? "text-danger fw-semibold" : ""}`}>{product.stock}</span>
                                    </td>
                                    <td title={restrictionText}>
                                      <span className="ck-truncate d-block text-muted">{restrictionText || "-"}</span>
                                    </td>
                                    <td title={`Code: ${product.barcode || "-"} - SKU: ${product.sku}`}>
                                      <span className="ck-truncate d-block" style={{ fontFamily: "monospace", letterSpacing: "0.04em" }}>{product.barcode || "-"}</span>
                                      <small className="text-muted ck-truncate d-block">SKU {product.sku}</small>
                                    </td>
                                    <td title={supplierName || "Sans fournisseur"}>
                                      <span className="ck-truncate d-block">{supplierName || <span className="text-muted">-</span>}</span>
                                    </td>
                                    <td>
                                      <div className="d-flex gap-1 align-items-center">
                                        <button className="ck-icon-btn" type="button" data-tip="Modifier" aria-label="Modifier le produit" onClick={(e) => { e.stopPropagation(); handleEdit(product); }}>
                                          <i className="fas fa-pen" />
                                        </button>
                                        <button className="ck-icon-btn" type="button" data-tip="Imprimer l'etiquette" aria-label="Imprimer l'etiquette" onClick={(e) => { e.stopPropagation(); printProductLabel(product); }}>
                                          <i className="fas fa-tag" />
                                        </button>
                                        <button className="ck-icon-btn ck-icon-btn-danger" type="button" data-tip="Supprimer" aria-label="Supprimer le produit" onClick={(e) => { e.stopPropagation(); handleDelete(product.id); }}>
                                          <i className="fas fa-trash" />
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                              {!paginatedProducts.length ? (
                                <tr>
                                  <td colSpan="10" className="text-muted">
                                    Aucun produit ne correspond a cette recherche.
                                  </td>
                                </tr>
                              ) : null}
                            </tbody>
                          </table>
                        </div>
                      <div className="d-flex justify-content-between align-items-center mt-4 flex-wrap gap-2">
                        <small className="text-muted">
                          Page {productPage} / {totalProductPages}
                        </small>
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-outline-dark btn-sm"
                            type="button"
                            onClick={() => setProductPage((current) => Math.max(1, current - 1))}
                            disabled={productPage === 1}
                          >
                            Precedent
                          </button>
                          <button
                            className="btn btn-outline-dark btn-sm"
                            type="button"
                            onClick={() =>
                              setProductPage((current) =>
                                Math.min(totalProductPages, current + 1)
                              )
                            }
                            disabled={productPage === totalProductPages}
                          >
                            Suivant
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MyAccountAddress;
